import { BrandButton, Typography, Colors, Link, Shadows, Animation, Spinner, Zindexes, ProgressBar, Text, Field, TextInput, Spacer, Icon, Grid } from '@zapier/design-system';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { css, Global } from '@emotion/react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { emit } from '@zapier/events-toolkit-browser';
import { captureException, captureMessage } from '@sentry/minimal';
import { addTruthyParamsToUrl, addParamsToUrl, extractParams, removeParamsFromUrl } from '@zapier/url-utils';
import { readCookie, writeCookie } from '@zapier/cookies';
import { urls, isInIframe as isInIframe$1, isCypress, mediaQueries } from '@zapier/identity-utils';
import fetch$1, { createFetch, responseBody } from '@zapier/identity-fetch';
import { ZxcvbnOptions, zxcvbn } from '@zxcvbn-ts/core';
import zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import { has, camelCase } from 'lodash';
import { isInIframe } from '@zapier/identity-utils/src';

function AuthButton(props) {
  return jsx(BrandButton, {
    isFullWidth: true,
    disabled: props.disabled,
    children: props.children
  });
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

/*
 * Related AVRO schema: https://gitlab.com/zapier/avro-schemas/-/blob/main/schemas/web_analytics/tracking/user_interaction/signup_user_interaction_event.avsc
 */
function dispatchSignupUserInteractionEvent({
  signupFunnel,
  signupType,
  selectedApis
}) {
  const signup_location = typeof window !== 'undefined' ? window.location.href : 'server-side';
  /*
   * Our eventing library pre-fills a bunch of fields for us, so we don't need to pass everything:
   * https://engineering.zapier.com/guides/event-streaming/creating-and-emitting-events/emitting-frontend-events/#autopopulated-fields
   *
   * We don't pass `account_id` because at this point the user doesn't have an account yet. Any account_id that might be
   * present in the browser, is from a previous session.
   */

  return emit({
    system_name: 'identity',
    timestamp_ms: new Date().getTime(),
    absolute_url: window.location.toString(),
    referrer: document.referrer,
    signup_funnel: signupFunnel,
    signup_type: signupType,
    selected_apis: selectedApis instanceof Array ? selectedApis.join(',') : undefined,
    signup_location
  }, 'web_analytics.tracking.user_interaction.SignupUserInteractionEvent');
}
/*
 * Created as part of ID-905: https://zapierorg.atlassian.net/browse/ID-905
 * For now we need to support both the old events and the new Avro events. This method provides an easy solution to
 * converting the old event type to the Avro counterpart.
 */

const eventTypeMap = {
  'facebook-sso': 'facebook_sso',
  sso: 'sso',
  'flab-abs': 'flab_abs',
  direct: 'direct',
  'flab-sso': 'flab_sso',
  'google-one-tap': 'google_one_tap',
  'google-sso': 'google_sso',
  'microsoft-sso': 'microsoft_sso',
  'one-tap': 'one_tap'
};
function oldSignupTypeToAvroSignupType(type) {
  return eventTypeMap[type];
}

const eventName = 'web_analytics.tracking.user_interaction.GoogleOneTapErrorEvent';
const avroFailureReasons = ['cancel_called', 'browser_not_supported', 'loading_timeout', 'opt_out_or_no_session', 'suppressed_by_user', 'auto_cancel', 'user_cancel'];
const avroFailureTypes = ['dismissed', 'display', 'skipped'];
function emitGoogleOneTapErrorEvent({
  failure_type = null,
  failure_reason = null,
  message = null,
  action
}) {
  const failure_reason_filtered = avroFailureReasons.includes(failure_reason || '') ? failure_reason : null;
  const failure_type_filtered = avroFailureTypes.includes(failure_type || '') ? failure_type : null;
  return emit({
    timestamp_ms: new Date().getTime(),
    failure_reason: failure_reason_filtered,
    failure_type: failure_type_filtered,
    message,
    location: 'SignUpSection',
    release_id: process.env.NEXT_PUBLIC_COMMIT_SHA,
    // During signup there is no account id
    account_id: null,
    action
  }, eventName);
}

var _templateObject$h, _templateObject2$a, _templateObject3$5;
const EVENT_CAPTCHA_RENDERED = 'signup: captcha rendered';
const EVENT_CAPTCHA_VERIFICATION_FAIL = 'signup: captcha verification failed';
const descriptionCss = css(_templateObject$h || (_templateObject$h = _taggedTemplateLiteral(["\n  ", "\n  margin-bottom: 24px;\n  text-align: center;\n"])), Typography.base);
const recaptchaCss = css(_templateObject2$a || (_templateObject2$a = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  margin-top: 30px;\n"])));
const rootCss$1 = css(_templateObject3$5 || (_templateObject3$5 = _taggedTemplateLiteral(["\n  background: ", ";\n"])), Colors.white);
/**
 * Shows a reCaptcha form to confirm the user isn't a robot
 */

function Captcha(props) {
  const {
    trackEvent
  } = useTrackingContext();
  useEffect(() => {
    // Track that we showed the Captcha to the user
    trackEvent(EVENT_CAPTCHA_RENDERED);
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_captcha_rendered',
      // I don't think that SSO or SAML ever require captcha
      signupType: 'direct'
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onExpired() {
    trackEvent(EVENT_CAPTCHA_VERIFICATION_FAIL, {
      reason: 'captcha expired'
    });
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_captcha_verification_failed',
      // I don't think that SSO or SAML ever require captcha
      signupType: 'direct'
    });

    if (props.onCaptchaExpiration) {
      props.onCaptchaExpiration();
    }
  }

  return jsxs("section", {
    css: rootCss$1,
    "data-testid": "auth-captcha",
    children: [jsx("div", {
      css: descriptionCss,
      children: "Looks like our robots want to make sure you're not one!"
    }), jsx(ReCAPTCHA, {
      css: recaptchaCss,
      onChange: props.onCaptchaVerification,
      onExpired: onExpired,
      sitekey: props.googleCaptchaSiteKey
    })]
  });
}

function defaultRouteTo$1(url) {
  window.location.href = url;
}

const LOCAL_STORAGE_ACCOUNT_ID = 'currentAccountId';
const DASHBOARD_FALLBACK_URL_NAME = 'dashboard'; // See https://zapier.slack.com/archives/C0328F7CB8D/p1650997437747439
// This shouldn't be around for longer than a couple months

const CAMPAIGN_PROJECT_HASHBROWN = 'HB_swwkbvdwmr0n07o'; // Used to determine what login mechanism a user used last.

const AUTH_TYPE_COOKIE_NAME = 'auth_type';

const fetch = createFetch({
  // Removes authenticationError middleware so we
  // do not throw errors on 401s since those are expected
  afterResponse: [responseBody]
});

/**
 * Attempts to discover the last account id that the user
 * logged in to
 */
function getStoredAccountId() {
  // TODO: first try useCurrentAccountId to see if the cookie
  // is available
  // Try to read stored currentAccount id from localStorage since
  // that will contain the id of the last account the user logged in to
  let storedAccountId = '';

  try {
    storedAccountId = localStorage.getItem(LOCAL_STORAGE_ACCOUNT_ID) || '';
  } catch (e) {// This is only used for backwards compatibility reasons, so if we cannot store it in LocalStorage for whatever reason, we can just ignore it.
  }

  const rawAccountId = Number.parseInt(storedAccountId);
  return rawAccountId && Number.isInteger(rawAccountId) && rawAccountId > 0 ? rawAccountId : null;
}
/**
 * Discovers which account an email address is associated with.
 * Uses most recently logged in account id (if available) with
 * a fallback to the default account to decide which account
 * the user wants to log in to
 */


async function discoverAccount({
  attemptStoredAccount,
  email
}) {
  // Try to find the last account that the user logged in as
  const storedAccountId = attemptStoredAccount ? getStoredAccountId() : undefined;
  let account; // Use account discovery to see if the user is trying to log into
  // an account that is SAML enabled

  const idpData = await fetch(addTruthyParamsToUrl(urls.ACCOUNT_DISCOVERY_URL, {
    account_id: storedAccountId,
    email
  }));
  account = idpData.body.account;

  if (!account && storedAccountId) {
    // This means the email and storedAccountId didn't match
    // so try making the request again w/o the storedAccountId
    // so we can just let the email dictate which account we are
    // logging in as
    const noAccountData = await fetch(addTruthyParamsToUrl(urls.ACCOUNT_DISCOVERY_URL, {
      email
    }));
    account = noAccountData.body.account;
  }

  return account ? {
    hasSamlSso: account.has_saml_sso,
    id: account.id,
    ssoUrl: account.sso_url
  } : account;
}

async function getInvitationByCode(code) {
  // Get invitation from API
  const result = await fetch$1("".concat(urls.INVITATION_API_URL, "/").concat(code, "/"));

  if (!result.ok) {
    const body = result.body; // Oh no! Something went wrong
    // Some errors are just returned as strings

    if (typeof body === 'string') {
      throw body;
    } // Look for possible error properties


    throw (body === null || body === void 0 ? void 0 : body.error) || (body === null || body === void 0 ? void 0 : body.detail);
  } // Success


  const body = result.body;
  return {
    code: body.code,
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso
    },
    email: body.email,
    id: body.id,
    status: body.status,
    ssoUrl: body.sso_url,
    createdBy: body.created_by
  };
}

async function declineInvitation(code) {
  const result = await fetch$1("".concat(urls.INVITATION_API_URL, "/").concat(code, "/decline"), {
    method: 'PUT'
  });

  if (!result.ok) {
    const body = result.body; // Oh no! Something went wrong
    // Some errors are just returned as strings

    if (typeof body === 'string') {
      throw body;
    } // Look for possible error properties


    throw (body === null || body === void 0 ? void 0 : body.error) || (body === null || body === void 0 ? void 0 : body.message);
  } // Success


  const body = result.body;
  return {
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso
    },
    email: body.email,
    id: body.id,
    code: body.code,
    status: body.status,
    createdBy: body.created_by,
    ssoUrl: body.sso_url
  };
}

/**
 * Given an invitation based on the given invitation code
 */
function useInvitation(code, options) {
  const [invitation, setInvitation] = useState(null);
  const [status, setStatus] = useState('initial');
  const [error, setError] = useState(null);
  useEffect(() => {
    async function maybeGetInvitation() {
      if (code && !(options !== null && options !== void 0 && options.skip)) {
        // Show we are loading something
        setStatus('loading');

        try {
          // Try to get invitation
          const invitation = await getInvitationByCode(code); // We got an invitation

          setInvitation(invitation);
          setError(null);
          setStatus('success');
        } catch (err) {
          setError(err);
          setStatus('error');
        }
      } else {
        // we didn't get a code so clear invitation
        setInvitation(null);
        setStatus('initial');
      }
    }

    maybeGetInvitation();
  }, [code, options === null || options === void 0 ? void 0 : options.skip]);

  async function maybeDeclineInvitation(invitationCode) {
    try {
      // Try to decline invitation
      const invitation = await declineInvitation(invitationCode); // Update the invitation

      setInvitation(invitation);
      setError(null);
      setStatus('success');
    } catch (err) {
      setError(err);
      setStatus('error');
    }
  }

  return {
    code,
    error,
    invitation,
    isValid: Boolean( // If there wasn't a code passed then we consider it valid since we aren't looking for an invitation
    !code || // If we haven't finished loading the invite then it can't be invalid yet
    status === 'initial' || status === 'loading' || // If we have an error then the invite is invalid
    // If the invitation status isn't 'pending' it means it's either
    // already been used or has expired so it's invalid
    !error && (invitation === null || invitation === void 0 ? void 0 : invitation.status) === 'pending'),
    declineInvitation: maybeDeclineInvitation,
    status
  };
}

const ACCOUNT_LOCKED_ERROR = 'account_locked';
const GENERIC_ERROR_MESSAGE$1 = 'Yikes! Something went wrong when trying to log in. If you keep having trouble, please contact support.';
const MFA_ERROR = 'mfa_required';
const SINGLE_SESSION_ERROR = 'single_session';

let LoginResultType;

(function (LoginResultType) {
  LoginResultType["IS_2FA_REQUIRED"] = "IS_2FA_REQUIRED";
  LoginResultType["IS_BAD_CREDENTIALS"] = "IS_BAD_CREDENTIALS";
  LoginResultType["IS_CAPTCHA_REQUIRED"] = "IS_CAPTCHA_REQUIRED";
  LoginResultType["IS_LOCKED"] = "IS_LOCKED";
  LoginResultType["IS_MISSING_CSRF"] = "IS_MISSING_CSRF";
  LoginResultType["IS_SINGLE_SESSION"] = "IS_SINGLE_SESSION";
  LoginResultType["IS_VALID_LOGIN"] = "IS_VALID_LOGIN";
  LoginResultType["TRYING_TOO_OFTEN"] = "TRYING_TOO_OFTEN";
  LoginResultType["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
})(LoginResultType || (LoginResultType = {}));

function hasErrorsOfType(type, errors) {
  return Array.isArray(errors) && errors.some(error => error.type === type);
}

const fieldConversions$1 = {
  captcha_required: 'captcha'
};
function parseLoginErrors(resp) {
  const parsedErrors = {};
  let resultType = LoginResultType.IS_VALID_LOGIN;
  let redirectUrl = '';

  try {
    var _resp$headers, _resp$headers$get;

    const errors = resp.body.errors;

    if (!((_resp$headers = resp.headers) !== null && _resp$headers !== void 0 && (_resp$headers$get = _resp$headers.get('content-type')) !== null && _resp$headers$get !== void 0 && _resp$headers$get.includes('application/json'))) {
      // The server responded with HTML which normally means cookies have
      // been disabled
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = LoginResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    }

    if (resp.status === 429) {
      // The server says we are trying to often
      parsedErrors.error = 'It looks like you are trying to login too often. If you’re having trouble, please contact support.';
      resultType = LoginResultType.TRYING_TOO_OFTEN;
    } else if (resp.status === 401) {
      // The user entered invalid credentials
      resultType = LoginResultType.IS_BAD_CREDENTIALS;
    } else if (resp.status === 403 && hasErrorsOfType(ACCOUNT_LOCKED_ERROR, errors)) {
      // The account the user is trying to log in to was locked
      parsedErrors.error = 'Your account has been locked.';
      resultType = LoginResultType.IS_LOCKED;
      redirectUrl = urls.ACCOUNT_LOCKED_URL;
    } else if (resp.status === 403 && hasErrorsOfType(MFA_ERROR, errors)) {
      // The credentials were good but MFA is required to fully log in
      resultType = LoginResultType.IS_2FA_REQUIRED;
      redirectUrl = urls.MFA_URL;
    } else if (resp.status === 403 && hasErrorsOfType(SINGLE_SESSION_ERROR, errors)) {
      // User is logged in with multiple sessions
      parsedErrors.error = 'A single session is required.';
      resultType = LoginResultType.IS_SINGLE_SESSION;
      redirectUrl = urls.SINGLE_SESSION_URL;
    } else if (resp.status === 403) {
      // User has cookies disabled and csrf cannot be set
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = LoginResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    } // See if we need to parse the errors array or not


    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0]; // Some errors are returned as a string

      if (firstError && typeof firstError === 'string') {
        parsedErrors.error = firstError; // We don't know what type of error this is so set UNKNOWN

        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      } // Contextual messages like "You are already logged in."
      // @ts-ignore


      if (firstError && typeof firstError === 'object' && firstError !== null && firstError !== void 0 && firstError.message) {
        // @ts-ignore
        parsedErrors.error = firstError === null || firstError === void 0 ? void 0 : firstError.message;

        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      } // One or more fields has returned an error


      errors.forEach(error => {
        if (typeof error === 'string') {
          return;
        }

        const fields = error === null || error === void 0 ? void 0 : error.fields;

        if (fields) {
          fields.forEach(field => {
            const key = fieldConversions$1[field.key] || field.key; // @ts-ignore

            parsedErrors[key] = field.message;
          });
        }
      });
      /**
       * We have an error, but we don't know how to deal with it. So throw
       * a generic error.
       */

      if (Object.keys(parsedErrors).length === 0) {
        throw resp;
      }
    }
  } catch (err) {
    /**
     * Something has blown up, maybe the API is down or the client might
     * be disconnected, or we have an error we cannot parse. In this case
     * send along a generic error.
     */
    parsedErrors.error = GENERIC_ERROR_MESSAGE$1;

    if (resultType === LoginResultType.IS_VALID_LOGIN) {
      resultType = LoginResultType.UNKNOWN_ERROR;
    }
  }

  if (resp.status === 422 && has(parsedErrors, 'captcha')) {
    resultType = LoginResultType.IS_CAPTCHA_REQUIRED;
  }

  return {
    errors: parsedErrors,
    redirectUrl,
    type: resultType
  };
}

function shouldShowSSOOption$1(authType, expectedSSOOption, forceLoginDisplayForm = true, ssoOptionEnabled) {
  // If we're in an iframe, Facebook is the only one that works.
  if (isInIframe() && expectedSSOOption !== 'facebook') {
    return false;
  } // No previous authentication type detected, or the user has indicated that they'd
  // like to see all available login options. Render all enabled SSO options.


  if (!authType || forceLoginDisplayForm) {
    return ssoOptionEnabled === undefined || ssoOptionEnabled === true;
  } // We've detected a previous auth type, render it.


  return (ssoOptionEnabled === undefined || ssoOptionEnabled === true) && authType === expectedSSOOption;
}

const defaultState$1 = {
  email: '',
  password: '',
  captcha: null
};
const defaultFlagState$1 = {
  captcha: false,
  email: false,
  password: false
};
/**
 * Attempts login via API and handles errors / SAML flow
 */

async function login(data, next, routeTo) {
  // First ensure we are logged out
  await logout(); // Try to discovery the user's account

  const discoveredAccount = await discoverAccount({
    attemptStoredAccount: true,
    email: data.email
  }); // Check for SAML redirect from account-discovery

  if (discoveredAccount !== null && discoveredAccount !== void 0 && discoveredAccount.hasSamlSso && discoveredAccount !== null && discoveredAccount !== void 0 && discoveredAccount.ssoUrl) {
    // We need to redirect to start SAML
    routeTo(addTruthyParamsToUrl(discoveredAccount.ssoUrl, {
      email: data === null || data === void 0 ? void 0 : data.email
    }));
    return {
      didRedirect: true
    };
  } // Attempt login


  const loginResult = await fetch(urls.LOGIN_API_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json'
    },
    body: {
      // If we found an account id from account-discovery
      account_id: discoveredAccount === null || discoveredAccount === void 0 ? void 0 : discoveredAccount.id,
      captcha: data.captcha,
      email: data.email,
      password: data.password
    }
  }); // Errors are in a hard to read format so reformat them
  // for easy consumption in the UI

  const errors = parseLoginErrors(loginResult); // See if we need to redirect the user

  if (errors.redirectUrl) {
    routeTo(addTruthyParamsToUrl(errors.redirectUrl, {
      next
    }));
  } // If there were no errors, and we successfully logged in, store the user's authentication method, so we can automatically
  // show this authentication method next time they try to log in.


  if (Object.keys(errors.errors || {}).length === 0) {
    storeAuthType('email');
  }

  return {
    account: discoveredAccount,
    didRedirect: Boolean(errors.redirectUrl),
    ...errors
  };
}

function getDefaultState$1(input) {
  return { ...defaultState$1,
    email: input.email || input.defaultEmail || defaultState$1.email
  };
}
/**
 * React hook that controls the data / flow of logging a user in
 */


function useLogin(input = {}) {
  const [formData, setFormData] = useState(getDefaultState$1(input)); // We want to always show the email input first, even if a password manager auto
  // fills it for the user. We don't want it skipping to the password page in this case

  const [hasClickedContinue, setHasClickedContinue] = useState(false);
  const [status, setStatus] = useState('initial');
  const [formErrors, setErrors] = useState({});
  const [submittedFields, setSubmittedFields] = useState({ ...defaultFlagState$1
  });
  const [touchedFields, setTouchedFields] = useState({ ...defaultFlagState$1
  });
  const authType = useAuthType();
  const isCaptchaRequired = Boolean(formErrors.captcha);
  const routeTo = input.routeTo || defaultRouteTo$1; // Only allow users to submit when the email field has something in it

  const canSubmit = formData.email.length > 0;
  const shouldShowPasswordField = shouldShowPassword();
  const shouldShowEmailField = !shouldShowPasswordField;

  function shouldShowPassword() {
    return (// If the user has an error with their email, we want to go back to
      // the email input and show it to them
      !formErrors.email && hasClickedContinue && Boolean( // If the user has submitted the email
      submittedFields.email && status === 'initial' || // There is an error (but not with the email field)
      status === 'error' && !formErrors.email && formErrors.password || // The user has submitted a password
      status === 'submitting' && touchedFields.password || // This catches password managers where the user auto-fills
      // the email and password. We can't use formData.password
      // in case the user deletes the password field completely
      // we don't want it flipping back to the email
      formData.email && touchedFields.password)
    );
  }

  const shouldShowFacebookSSO = shouldShowSSOOption$1(authType, 'facebook', input.forceLoginDisplayForm, input.facebookSSOEnabled);
  const shouldShowGoogleSSO = shouldShowSSOOption$1(authType, 'google', input.forceLoginDisplayForm, input.googleSSOEnabled);
  const shouldShowMicrosoftSSO = shouldShowSSOOption$1(authType, 'microsoft', input.forceLoginDisplayForm, input.microsoftSSOEnabled);
  const shouldShowEmailPasswordForm = input.forceLoginDisplayForm || !authType || authType === 'email';
  useEffect(() => {
    // force the input email if the input changes
    if (input.email || input.defaultEmail) {
      setFormData(data => ({ ...data,
        email: input.email || input.defaultEmail || ''
      }));
    } // If the user entered the captcha on the last handleChange
    // then go ahead and submit the form since there's no
    // submit button visible


    if (formData.captcha) {
      handleSubmit();
    } // Adding the handleSubmit would cause this to run on every
    // rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [input.email, input.defaultEmail, formData.captcha]);
  /**
   * Handles changes in form data
   */

  function handleChange(field, value) {
    setFormData(data => ({ ...data,
      // Do not allow field to change if passed in as props
      // @ts-ignore
      [field]: input[field] || value
    })); // Capture that the user has changed this field

    setTouchedFields(state => ({ ...state,
      [field]: true
    }));
  }

  function handleClearForm() {
    setFormData(getDefaultState$1(input));
    setHasClickedContinue(false);
    setErrors({});
    setSubmittedFields({ ...defaultFlagState$1
    });
    setTouchedFields({ ...defaultFlagState$1
    });
    setStatus('initial');
  }

  async function handleSubmit(e) {
    if (e) {
      e.preventDefault();
    } // Show that we are submitting


    setStatus('submitting');
    setSubmittedFields({
      captcha: Boolean(formData.captcha),
      email: Boolean(formData.email),
      password: hasClickedContinue && Boolean(formData.password)
    }); // Attempt login

    try {
      var _loginResult;

      let loginResult;

      if (!hasClickedContinue) {
        loginResult = await login({
          email: formData.email
        }, input.next, routeTo);
      } else {
        loginResult = await login(formData, input.next, routeTo);
      }

      setHasClickedContinue(true); // Bail if we had to redirect and allow the redirect
      // to happen

      if (loginResult.didRedirect) return;

      if (Object.keys(((_loginResult = loginResult) === null || _loginResult === void 0 ? void 0 : _loginResult.errors) || {}).length > 0) {
        var _loginResult2;

        // We encountered errors and couldn't log in
        setErrors(((_loginResult2 = loginResult) === null || _loginResult2 === void 0 ? void 0 : _loginResult2.errors) || {});
        setStatus('error');
      } else {
        // Success
        setErrors({});
        setStatus('success'); // Route the user to the next location or the dashboard

        if (!input.next || input.next === urls.DEFAULT_LOGIN_NEXT) {
          routeTo(urls.DEFAULT_LOGIN_NEXT);
        } else {
          const redirectUrl = addParamsToUrl(urls.REDIRECT_URL, {
            next: input.next,
            safe_only: true,
            fallback: DASHBOARD_FALLBACK_URL_NAME
          });
          routeTo(redirectUrl);
        }
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      setErrors({
        error: GENERIC_ERROR_MESSAGE$1
      });
      setStatus('error');
    }
  }

  return {
    // Is the form allowed to be submitted
    canSubmit,
    // Current field data in the form
    formData,
    // Current errors in the form including field level errors and more generic form-level errors
    formErrors,
    // Callback to handle changes to form data
    handleChange,
    // Callback to handle clearing the form and resetting to default state
    handleClearForm,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Captcha input is required when true
    isCaptchaRequired,
    // Previous login detected from auth_type cookie
    previousAuthType: authType,
    // Should show the email field be shown in the email/password form
    shouldShowEmailField,
    // Should the email/password form be shown
    shouldShowEmailPasswordForm,
    // Should Facebook SSO button be shown
    shouldShowFacebookSSO,
    // Should Google SSO button be shown
    shouldShowGoogleSSO,
    // Should Microsoft SSO button be shown
    shouldShowMicrosoftSSO,
    // Should show the password field be shown in the email/password form
    shouldShowPasswordField,
    // Form status
    status,
    // Fields that have been submitted to the API
    submittedFields
  };
}

const GENERIC_ERROR_MESSAGE = 'Yikes! Something went wrong when trying to sign up. If you keep having trouble, please contact support.';

let SignupResultType;

(function (SignupResultType) {
  SignupResultType["IS_MISSING_CSRF"] = "IS_MISSING_CSRF";
  SignupResultType["IS_VALID_SIGNUP"] = "IS_VALID_SIGNUP";
  SignupResultType["TRYING_TOO_OFTEN"] = "TRYING_TOO_OFTEN";
  SignupResultType["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
})(SignupResultType || (SignupResultType = {}));

// server will sometimes return with different keys

const fieldConversions = {
  captcha_required: 'captcha',
  password1: 'password'
};
function parseSignupErrors(resp) {
  const parsedErrors = {};
  let resultType = SignupResultType.IS_VALID_SIGNUP;
  let redirectUrl = '';

  try {
    var _resp$headers, _resp$headers$get;

    const errors = resp.body.errors;

    if (!((_resp$headers = resp.headers) !== null && _resp$headers !== void 0 && (_resp$headers$get = _resp$headers.get('content-type')) !== null && _resp$headers$get !== void 0 && _resp$headers$get.includes('application/json'))) {
      // The server responded with HTML which normally means cookies have
      // been disabled
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = SignupResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    }

    if (resp.status === 429) {
      // The server says we are trying to often
      parsedErrors.error = 'It looks like you are trying too often. If you’re having trouble, please contact support.';
      resultType = SignupResultType.TRYING_TOO_OFTEN;
    } // See if we need to parse the errors array or not


    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0]; // Some errors are returned as a string

      if (firstError && typeof firstError === 'string') {
        parsedErrors.error = firstError; // We don't know what type of error this is so set UNKNOWN

        if (resultType === SignupResultType.IS_VALID_SIGNUP) {
          resultType = SignupResultType.UNKNOWN_ERROR;
        }
      } // Contextual messages like "You are already logged in."
      // @ts-ignore


      if (firstError && typeof firstError === 'object' && firstError !== null && firstError !== void 0 && firstError.message) {
        // @ts-ignore
        parsedErrors.error = firstError === null || firstError === void 0 ? void 0 : firstError.message;

        if (resultType === SignupResultType.IS_VALID_SIGNUP) {
          resultType = SignupResultType.UNKNOWN_ERROR;
        }
      } // One or more fields has returned an error


      errors.forEach(error => {
        if (typeof error === 'string') {
          return;
        }

        const fields = error === null || error === void 0 ? void 0 : error.fields;

        if (fields) {
          fields.forEach(field => {
            // Coalesce inconsistent server field keys with fieldConversions
            // otherwise camelCase the keys
            const key = fieldConversions[field.key] || camelCase(field.key); // @ts-ignore

            parsedErrors[key] = field.message;
          });
        }
      });
      /**
       * We have an error, but we don't know how to deal with it. So throw
       * a generic error.
       */

      if (Object.keys(parsedErrors).length === 0) {
        throw resp;
      }
    }
  } catch (err) {
    /**
     * Something has blown up, maybe the API is down or the client might
     * be disconnected, or we have an error we cannot parse. In this case
     * send along a generic error.
     */
    parsedErrors.error = GENERIC_ERROR_MESSAGE;

    if (resultType === SignupResultType.IS_VALID_SIGNUP) {
      resultType = SignupResultType.UNKNOWN_ERROR;
    }
  }

  return {
    errors: parsedErrors,
    redirectUrl,
    type: resultType
  };
}

function useAuthType() {
  return useMemo(() => getAuthTypeFromCookie(), []);
}

function getAuthTypeFromCookie() {
  return readCookie(AUTH_TYPE_COOKIE_NAME);
}

function signupTypeToAuthType(signupType) {
  const typeMap = {
    direct: 'email',
    'google-sso': 'google',
    'facebook-sso': 'facebook',
    'microsoft-sso': 'microsoft'
  };
  return typeMap[signupType];
}
function storeAuthType(authType) {
  writeCookie(AUTH_TYPE_COOKIE_NAME, authType, {
    category: 'essential' // We can't write this cookie if the user has not consented. Something to keep in mind.

  });
}
function removeStoredAuthType() {
  writeCookie(AUTH_TYPE_COOKIE_NAME, '', {
    category: 'essential',
    // We can't write this cookie if the user has not consented. Something to keep in mind.
    expires: new Date(Date.now() - 10)
  });
}

const defaultState = {
  email: '',
  firstName: '',
  lastName: '',
  password: ''
};
const defaultFlagState = {
  captcha: false,
  email: false,
  firstName: false,
  lastName: false,
  password: false
};

function defaultRouteTo(url) {
  window.location.href = url;
}

async function signup(data, next, selectedApis, routeTo) {
  var _signupResult$body;

  // First ensure we are logged out
  await logout();
  const body = {
    captcha: data.captcha,
    email: data.email,
    first_name: data.firstName,
    last_name: data.lastName,
    // API requires this field to be duplicated
    password: data.password,
    password1: data.password,
    selected_apis: selectedApis
  };

  if (data.invitation) {
    body.invitation = data.invitation;
  } // Attempt to sign user up


  const signupResult = await fetch(urls.SIGNUP_API_URL, {
    method: 'POST',
    body
  }); // Errors are in a hard to read format so reformat them
  // for easy consumption in the UI

  const errors = parseSignupErrors(signupResult); // Look for SAML issues

  if (Object.values(errors.errors).some(e => e === null || e === void 0 ? void 0 : e.includes('SAML'))) {
    // Try to discovery the user's email requires SAML
    const discoveredAccount = await discoverAccount({
      attemptStoredAccount: false,
      email: data.email
    }); // Check for SAML redirect from account-discovery

    if (discoveredAccount !== null && discoveredAccount !== void 0 && discoveredAccount.hasSamlSso && discoveredAccount !== null && discoveredAccount !== void 0 && discoveredAccount.ssoUrl) {
      // We need to redirect to start SAML
      routeTo(addTruthyParamsToUrl(discoveredAccount.ssoUrl, {
        email: data === null || data === void 0 ? void 0 : data.email
      }));
      return {
        didRedirect: true
      };
    }
  } // See if we need to redirect the user


  if (errors.redirectUrl) {
    routeTo(addTruthyParamsToUrl(errors.redirectUrl, {
      next
    }));
  } // If there were no errors, and we successfully signed up, store the user's authentication method, so we can automatically
  // show this authentication method next time they try to log in.


  if (Object.keys(errors.errors || {}).length === 0) {
    storeAuthType('email');
  }

  return {
    didRedirect: false,
    userId: signupResult === null || signupResult === void 0 ? void 0 : (_signupResult$body = signupResult.body) === null || _signupResult$body === void 0 ? void 0 : _signupResult$body.id,
    ...errors
  };
}

function getDefaultState(input) {
  return { ...defaultState,
    email: input.email || defaultState.email,
    invitation: input.invitation
  };
}

function shouldShowPassword(formData, formErrors, status, submittedFields, touchedFields) {
  // If the user has submitted the email
  if (submittedFields.email && status === 'initial') return true; // There is an error (but not with the email field)

  if (status === 'error' && !formErrors.email && !formErrors.firstName && !formErrors.lastName && formErrors.password) return true; // The user has submitted a password

  if (status === 'submitting' && touchedFields.password) return true; // This catches password managers where the user auto-fills
  // the email and password. We can't use formData.password
  // in case the user deletes the password field completely
  // we don't want it flipping back to the email

  if (formData.email && touchedFields.password) return true;
  return false;
}
/**
 * React hook that controls the data / flow of signing a user up for Zapier
 */


function useSignup(input = {}) {
  const [formData, setFormData] = useState(getDefaultState(input));
  const [status, setStatus] = useState('initial');
  const [hasTouched, setHasTouched] = useState(false);
  const [formErrors, setErrors] = useState({});
  const [submittedFields, setSubmittedFields] = useState({ ...defaultFlagState
  });
  const [touchedFields, setTouchedFields] = useState({ ...defaultFlagState
  });
  const {
    trackSignupIntent
  } = useTrackingContext();
  const isCaptchaRequired = Boolean(formErrors.captcha);
  const routeTo = input.routeTo || defaultRouteTo;
  const shouldShowPasswordField = shouldShowPassword(formData, formErrors, status, submittedFields, touchedFields);
  const shouldShowEmailField = !shouldShowPasswordField;
  const shouldShowNameFields = (hasTouched || !input.hideFieldsUntilFocus) && !shouldShowPasswordField;
  const shouldShowFacebookSSO = shouldShowSSOOption('facebook', input.facebookSSOEnabled);
  const shouldShowGoogleSSO = shouldShowSSOOption('google', input.googleSSOEnabled);
  const shouldShowMicrosoftSSO = shouldShowSSOOption('microsoft', input.microsoftSSOEnabled);
  useEffect(() => {
    // force the input email if the input changes
    if (input.email) {
      setFormData(data => ({ ...data,
        email: input.email || ''
      }));
    }
  }, [input.email]);
  useEffect(() => {
    // If the user entered the captcha on the last handleChange
    // then go ahead and submit the form since there's no
    // submit button visible
    if (formData.captcha) {
      handleSubmit();
    } // Adding the handleSubmit would cause this to run on every
    // rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [formData.captcha]);
  /**
   * Handles changes in form data
   */

  function handleChange(field, value) {
    setFormData(data => ({ ...data,
      // Do not allow field to change if passed in as props
      // @ts-ignore
      [field]: input[field] || value
    })); // Capture that the user has changed this field

    setTouchedFields(state => ({ ...state,
      [field]: true
    }));
  }

  function handleClearForm() {
    setFormData(getDefaultState(input));
    setErrors({});
    setSubmittedFields({ ...defaultFlagState
    });
    setTouchedFields({ ...defaultFlagState
    });
    setStatus('initial');
  }

  function handleFocus() {
    if (!hasTouched) {
      trackSignupIntent('direct', {
        selected_apis: input.selectedApis
      });
    }

    setHasTouched(true);
  }

  async function handleSubmit(e) {
    if (e) {
      e.preventDefault();
    } // Show that we are submitting


    setStatus('submitting');
    setSubmittedFields({
      captcha: Boolean(formData.captcha),
      email: Boolean(formData.email),
      firstName: Boolean(formData.firstName),
      lastName: Boolean(formData.lastName),
      password: Boolean(formData.password)
    }); // Attempt signup

    try {
      const signupResult = await signup(formData, input.next, input.selectedApis, routeTo); // Bail if we had to redirect and allow the redirect
      // to happen

      if (signupResult.didRedirect) return;

      if (Object.keys((signupResult === null || signupResult === void 0 ? void 0 : signupResult.errors) || {}).length > 0) {
        // We encountered errors and couldn't log in
        setErrors((signupResult === null || signupResult === void 0 ? void 0 : signupResult.errors) || {});
        setStatus('error');
      } else {
        // Success
        setErrors({});
        setStatus('success');
        const nextWithParams = addTruthyParamsToUrl(input.next || urls.DEFAULT_SIGNUP_NEXT, {
          selected_apis: input.selectedApis,
          invitation_code: formData.invitation
        }); // Route the user to the next location or the dashboard

        routeTo(nextWithParams);
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      setErrors({
        error: GENERIC_ERROR_MESSAGE
      });
      setStatus('error');
    }
  }

  return {
    // Current field data in the form
    formData,
    // Current errors in the form including field level errors and more generic form-level errors
    formErrors,
    // Callback to handle changes to form data
    handleChange,
    // Callback to handle clearing the form and resetting to default state
    handleClearForm,
    // Callback to handle when a field is focused
    handleFocus,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Will be true if the user has focused and fields in the form
    hasTouched,
    // Captcha input is required when true
    isCaptchaRequired,
    // Should show the email field be shown in the email/password form
    shouldShowEmailField,
    // Should show the first / last name fields be shown in the email/password form
    shouldShowNameFields,
    // Should show the password field be shown in the email/password form
    shouldShowPasswordField,
    // Should Facebook SSO button be shown
    shouldShowFacebookSSO,
    // Should Google SSO button be shown
    shouldShowGoogleSSO,
    // Should Microsoft SSO button be shown
    shouldShowMicrosoftSSO,
    // Form status
    status,
    // Fields that have been submitted to the API
    submittedFields
  };
}

function shouldShowSSOOption(expectedSSOOption, ssoOptionEnabled) {
  // If we're in an iframe, Facebook is the only one that works.
  if (isInIframe$1() && expectedSSOOption !== 'facebook') {
    return false;
  }

  return ssoOptionEnabled === undefined || ssoOptionEnabled === true;
}

async function submitTwoFactor(data, next, routeTo) {
  const isRecovery = data === null || data === void 0 ? void 0 : data.recovery_code;
  const resp = await fetch(urls.TWO_FACTOR_AUTH_API_URL, {
    method: 'POST',
    body: data,
    headers: {
      Accept: 'application/json'
    }
  });

  if (resp.status === 200) {
    // Yay! Success
    // If the user has successfully logged in via 2FA, store the user's authentication method, so we can automatically
    // show this authentication method next time they try to log in.
    storeAuthType('email');

    if (!next || next === urls.DEFAULT_LOGIN_NEXT) {
      routeTo(urls.DEFAULT_LOGIN_NEXT);
    } else {
      const redirectUrl = addParamsToUrl(urls.REDIRECT_URL, {
        next: next,
        safe_only: true,
        fallback: DASHBOARD_FALLBACK_URL_NAME
      });
      routeTo(redirectUrl);
    }

    return {
      didRedirect: true
    };
  } else if (resp.status === 401) {
    return {
      didRedirect: false,
      error: 'Not authenticated'
    };
  } else if (resp.status === 403) {
    return {
      didRedirect: false,
      error: isRecovery ? 'That recovery code doesn’t look right, or it has been used before.' : 'That code doesn’t look right.'
    };
  } else if (resp.status == 429) {
    return {
      didRedirect: false,
      error: 'You’re trying too frequently.'
    };
  } else {
    // Unknown error
    return {
      didRedirect: false,
      error: 'Yikes! Something went wrong. If you keep having trouble, please contact support.'
    };
  }
}

function useTwoFactor({
  codeType = 'code',
  next = urls.DEFAULT_LOGIN_NEXT,
  routeTo = defaultRouteTo$1
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState();
  const [status, setStatus] = useState('initial'); // If the code type changes reset the form

  useEffect(() => {
    setCode('');
  }, [codeType]);

  async function handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    setStatus('submitting');
    setError(undefined);
    const result = await submitTwoFactor({
      [codeType]: code
    }, next, routeTo);
    setError(result.error);
    setStatus(result.error ? 'initial' : 'success');
  }

  function handleChangeCode(value) {
    setCode(value);
  }

  return {
    // Is the form allowed to be submitted
    canSubmit: Boolean(code),
    // Current field value
    code,
    // Type of code user is going to submit
    codeType,
    // Error returned from API
    error,
    // Callback to handle changes to form data
    handleChangeCode,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Form status
    status
  };
}

function useAutoFocus(shouldFocus) {
  const ref = useRef(null);
  useEffect(() => {
    if (shouldFocus) {
      var _ref$current;

      (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.focus();
    }
  }, [ref, shouldFocus]);
  return ref;
}

/**
 * Log the user out
 */

function logout() {
  removeStoredAuthType();
  return fetch(urls.LOGOUT_API_URL, {
    headers: {
      Accept: 'application/json'
    }
  });
}

var _templateObject$g;
const rebrandedLinkStyles$1 = css(_templateObject$g || (_templateObject$g = _taggedTemplateLiteral(["\n  &:focus {\n    outline: none;\n  }\n"])));
/**
 * Wrapper to start managed SSO flows where we have to listen for
 * messages from the SSO provider to know when it has completed
 */

function SSOButton({
  children,
  href,
  next,
  onTrackSignupConversion,
  onTrackSignupIntent,
  params = {},
  passThrough = false,
  routeTo = defaultRouteTo$1,
  shouldOpenInChildWindow = true,
  type
}) {
  // Build SSO start url
  const ssoStartUrl = addParamsToUrl(href, {
    in_child_window: shouldOpenInChildWindow,
    next,
    ...params
  });
  const onAuthComplete = useCallback(async event => {
    let data = {}; // Try to parse event data

    try {
      data = JSON.parse(event.data);

      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
    } catch (e) {
      // We couldn't parse the event data so log and bail
      captureException(e, {
        extra: {
          eventData: event.data
        }
      });
      return;
    } // Try to extract nextUrl from event data


    const {
      nextUrl
    } = data;

    if (!nextUrl) {
      // We couldn't extract the nextUrl so log and bail
      captureMessage('SSOButton: Error deciphering next URL from posted window message. nextUrl is empty');
      return;
    } // Get params from nextUrl so we know if this is a signup
    // or a login and get the id of the user that authed


    const params = extractParams(nextUrl); // See if this is a signup

    if (params.new_signup && onTrackSignupConversion) {
      try {
        // await so we don't switch pages before we tracked the event
        await onTrackSignupConversion(type, params.customuser_id);
      } catch (e) {
        // Event tracking failed but don't stop the flow just
        // log the error
        captureException(e);
      }
    } // Sanitize the nextUrl so we don't keep their user id in there


    const redirectUrl = removeParamsFromUrl(nextUrl, ['customuser_id']); // Redirect the user to the sanitized url

    routeTo(redirectUrl);
  }, [onTrackSignupConversion, routeTo, type]);
  const onMessage = useCallback(event => {
    // If we eventually want to ignore certain events this would
    // be the place to add that
    onAuthComplete(event);
  }, [onAuthComplete]);
  useEffect(() => {
    // Ensure that we stop listening for events on dismount
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);
  const onClick = useCallback(e => {
    e.preventDefault();
    const authType = signupTypeToAuthType(type);

    if (authType) {
      storeAuthType(authType);
    }

    if (passThrough && next) {
      // Go directly to href without tracking
      routeTo(next);
      return;
    } // Track that the user wanted to sign up


    if (onTrackSignupIntent) {
      onTrackSignupIntent(type);
    }

    if (shouldOpenInChildWindow) {
      // Starting the auth flow in a child window so add an event listener
      // so we know when the auth has completed
      window.addEventListener('message', onMessage); // Open the child window

      window.open(ssoStartUrl, 'popupwindow', 'height=600,width=980,resizable=yes,scrollbars=yes');
    } else {
      // Start the flow in current window
      routeTo(ssoStartUrl);
    }
  }, [next, onMessage, onTrackSignupIntent, passThrough, routeTo, shouldOpenInChildWindow, ssoStartUrl, type]);
  return jsx(Link, {
    href: ssoStartUrl,
    onClick: onClick,
    css: rebrandedLinkStyles$1,
    tabIndex: -1,
    children: children
  });
}

function getStartUrlForProvider(provider) {
  return "/sso/start?provider=".concat(provider);
}

var _templateObject$f, _templateObject2$9, _templateObject3$4, _templateObject4$2, _templateObject5$1, _templateObject6$1;
const buttonStyles = css(_templateObject$f || (_templateObject$f = _taggedTemplateLiteral(["\n  ", ";\n\n  align-items: center;\n  appearance: none;\n  background-color: ", ";\n  border-radius: 10px;\n  border: none;\n  box-shadow: ", ";\n  box-sizing: border-box;\n  color: ", ";\n  cursor: pointer;\n  height: 40px;\n  /* Safari applies 2px margin to buttons this clears it */\n  margin: 0;\n  min-width: 0;\n  opacity: 1;\n  position: relative;\n  text-decoration: none;\n  text-transform: none;\n  transition: ", ";\n  vertical-align: middle;\n  white-space: nowrap;\n  width: 100%;\n\n  &::-moz-focus-inner {\n    padding: 0;\n    border: 0;\n  }\n\n  &:hover,\n  &:focus {\n    background-color: ", ";\n  }\n"])), Typography.smallPrint1Bold, Colors.ocean, Shadows.elevation5, Colors.white, Animation.transitionValue, Colors.ocean120);

const rebrandedButtonStyles = color => css(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteral(["\n  ", ";\n  color: ", ";\n  border-radius: 3px;\n  height: 44px;\n  box-shadow: none;\n\n  // This is the outline that renders around the button when\n  // its focused. Its a pseudo element to preserve transparency;\n  // between the edge of the button and the edge of the outline.\n  // Its defined here vs only in ':focus' so that it can transition\n  // between states.\n  &::before {\n    content: '';\n    position: absolute;\n    pointer-events: none;\n    top: 0;\n    right: 0;\n    bottom: 0;\n    left: 0;\n    border: 2px solid transparent;\n    border-radius: 5px;\n    transition-property: all;\n    transition-timing-function: ", ";\n    transition-duration: 200ms;\n  }\n\n  &:focus:not([disabled]),\n  a:focus & {\n    background-color: ", ";\n    outline: none;\n    &::before {\n      border-color: ", ";\n      top: -5px;\n      right: -5px;\n      bottom: -5px;\n      left: -5px;\n    }\n  }\n"])), Typography.paragraph3Semibold, Colors.neutral100, Animation.transitionTimingFunction, color ? color.backgroundColor : Colors.blue, color ? color.backgroundColor : Colors.blue);

const busyStyles = css(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteral(["\n  align-items: center;\n  background-color: ", ";\n  display: flex;\n  justify-content: center;\n"])), Colors.ocean120);
const rebrandedBusyStyles = css(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteral(["\n  background-color: ", ";\n"])), Colors.night);
const mediumStyle = css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n  ", "\n  height: 50px;\n"])), Typography.paragraph3);
/**
 * This Button override is only meant to be used with non-standard
 * ZDS colors until we redesign the form with ZDS standard buttons
 */

function Button({
  children,
  color,
  disabled = false,
  isBusy = false,
  size = 'small',
  ...props
}) {
  const colorStyles = color ? css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n        background-color: ", ";\n        &:hover,\n        &:focus {\n          background-color: ", ";\n        }\n      "])), color.backgroundColor, color.hoverBackgroundColor) : undefined;
  return jsx("button", {
    css: [buttonStyles, rebrandedButtonStyles(color), colorStyles, isBusy && busyStyles, isBusy && rebrandedBusyStyles, size === 'medium' && mediumStyle],
    disabled: isBusy || disabled,
    ...props,
    children: isBusy ? jsx(Spinner, {
      color: "white"
    }) : children
  });
}

var _templateObject$e, _templateObject2$8;
const wrapperStyle = css(_templateObject$e || (_templateObject$e = _taggedTemplateLiteral(["\n  align-items: center;\n  display: flex;\n  height: 26px;\n  justify-content: center;\n  left: 0;\n  position: absolute;\n  top: 0;\n  width: 26px;\n\n  margin: 9px 10px;\n  background-color: ", ";\n  border-radius: 3px;\n"])), Colors.neutral100);
const iconStyles = css(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteral(["\n  align-items: center;\n  border-radius: 0;\n  display: flex;\n  height: 17.33px;\n  justify-content: center;\n  width: 17.33px;\n"])));
/**
 * Wrapper to style SSO buttons (i.e. facebook)
 */

function SocialAuthButton({
  children,
  icon,
  ...buttonProps
}) {
  return jsxs(Button, { ...buttonProps,
    children: [jsx("div", {
      css: wrapperStyle,
      children: jsx("div", {
        css: iconStyles,
        children: icon
      })
    }), children]
  });
}

const socialAppColors = {
  google: {
    backgroundColor: 'rgb(66, 133, 244)',
    hoverBackgroundColor: 'rgb(51, 103, 214)'
  },
  facebook: {
    backgroundColor: '#3b5999',
    hoverBackgroundColor: '#3b5999'
  },
  microsoft: {
    backgroundColor: Colors.neutral800,
    hoverBackgroundColor: Colors.neutral700
  },
  saml: {
    backgroundColor: Colors.zapier,
    hoverBackgroundColor: Colors.lava80
  }
};

function FacebookLogo() {
  return jsxs("svg", {
    fill: socialAppColors.facebook.backgroundColor,
    viewBox: "0 0 408.788 408.788",
    width: "21",
    height: "21",
    children: [jsx("title", {
      children: "Facebook Logo"
    }), jsx("path", {
      d: "M353.701,0H55.087C24.665,0,0.002,24.662,0.002,55.085v298.616c0,30.423,24.662,55.085,55.085,55.085 h147.275l0.251-146.078h-37.951c-4.932,0-8.935-3.988-8.954-8.92l-0.182-47.087c-0.019-4.959,3.996-8.989,8.955-8.989h37.882 v-45.498c0-52.8,32.247-81.55,79.348-81.55h38.65c4.945,0,8.955,4.009,8.955,8.955v39.704c0,4.944-4.007,8.952-8.95,8.955 l-23.719,0.011c-25.615,0-30.575,12.172-30.575,30.035v39.389h56.285c5.363,0,9.524,4.683,8.892,10.009l-5.581,47.087 c-0.534,4.506-4.355,7.901-8.892,7.901h-50.453l-0.251,146.078h87.631c30.422,0,55.084-24.662,55.084-55.084V55.085 C408.786,24.662,384.124,0,353.701,0z"
    })]
  });
}

function FacebookSSOButton({
  children = 'Continue with Facebook',
  next,
  onTrackSignupConversion,
  onTrackSignupIntent,
  params = {},
  routeTo
}) {
  return jsx(SSOButton, {
    href: getStartUrlForProvider('facebook'),
    next: next,
    onTrackSignupConversion: onTrackSignupConversion,
    onTrackSignupIntent: onTrackSignupIntent,
    params: params,
    routeTo: routeTo,
    type: "facebook-sso",
    children: jsx(SocialAuthButton, {
      color: socialAppColors.facebook,
      icon: jsx(FacebookLogo, {}),
      tabIndex: undefined,
      children: children
    })
  });
}

// This parent ID is used by the Javascript Google One Tap API, it allows us
// to mount One Tap in the wrapper rendered in the component's render method.
// This allows us to style the parent as we see fit.
const GOOGLE_ONE_TAP_PARENT_ID = 'google-one-tap-container'; // 24 hours measured in seconds - 24hrs is default

const GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION = 86400;
const GOOGLE_ONE_TAP_LOADING_TIMEOUT = 3000;
const EVENT_ONE_TAP_GOOGLE_IFRAME_CLOSED = 'google yolo iframe closed by user';
const EVENT_ONE_TAP_GOOGLE_IFRAME_LOAD_FAILED = 'google yolo iframe failed to load';
const HUMAN_READABLE_MESSAGE = {
  browser_not_supported: 'The current browser is not supported by Google One Tap.',
  invalid_client: 'An invalid `client_id` was provided. Check your Google Client ID and ensure it is configured correctly.',
  issuing_failed: 'Google failed to issue a credential. The selected session may have signed out of Google.',
  opt_out_or_no_session: 'There are no sessions available to be used by Google One Tap. Possible causes are that you are not logged in to Google or that an ad-blocker is preventing you from seeing your Google session.',
  suppressed_by_user: "The user previously opt-ed out of Google One Tap. The dialog will be suppressed until the suppression timeout period ellapses (".concat(GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION, " seconds) or cookies are cleared."),
  user_cancel: 'The user opt-ed out of the Google One Tap dialog by clicking the cancel button.'
};

var _templateObject$d;

function getHumanReadableMessage(reason) {
  const message = HUMAN_READABLE_MESSAGE[reason] || "No human-readable message available for: ".concat(reason, ". See official docs for additional info. https://developers.google.com/identity/one-tap/web/reference/js-reference?authuser=1#google.accounts.id.prompt");
  return "[Google One Tap]: ".concat(message);
} // Fixes TS error about `window` and `document` not existing on `global` API


const globalAny = global;
function GoogleOneTap({
  className,
  displayContext = 'use',
  googleClientId,
  onError,
  onGoogleOneTapLoaded,
  onSuccess,
  shouldAutologin = false,
  width = '100%'
}) {
  // We store if Google One Tap has successfully sent a notification to indicate when it has been initialized
  // we use this later to determine if One Tap has loaded before the `GOOGLE_ONE_TAP_LOADING_TIMEOUT` has expired.
  const googleOneTapIsLoaded = useRef(false); // We store if the Google One Tap script has loaded. This is different from the other loading state in that this just
  // stores if the script has loaded in the browser, not if One Tap is actually initialized. We need to know if the script
  // Has loaded before we can start initializing One Tap.

  const [googleOneTapScriptHasLoaded, setGoogleOneTapScriptHasLoaded] = useState(false); // We wrap the initialization in a `useEffect` callback so we correctly update any Google One Tap settings if the props
  // of this component change. An example of this is when someone uses query params in the `onSuccess` callback.
  // The `onSuccess` callback might depend on data that is not immediately available when the component is rendered so we
  // need to update the One Tap configuration when the `onSuccess` callback changes.

  useEffect(() => {
    var _globalAny$window, _globalAny$window$goo, _globalAny$window$goo2, _globalAny$window$goo3, _globalAny$window$goo4, _globalAny$window$goo5, _globalAny$window2, _globalAny$window2$go, _globalAny$window2$go2, _globalAny$window2$go3;

    // If the script is not currently loaded in the browser, the required globals won't be available yet. This UseEffect
    // depends on `googleOneTapScriptHasLoaded` so it runs again when the script has been loaded.
    if (!googleOneTapScriptHasLoaded) return;

    const onNotification = notification => {
      // This callback is fired when something occurs with Google One Tap.
      // This can be positive, or negative, so we must inspect the notification
      // in order to determine if a course of action must be taken (e.g. hide
      // Google One Tap)
      // For more information on the API and the error messages here, see the
      // official docs here: https://developers.google.com/identity/one-tap/web/reference/js-reference?authuser=1#google.accounts.id.prompt
      const momentType = notification.getMomentType(); // Display moment: This occurs after the prompt() method is called. The
      // notification contains a boolean value to indicate whether the UI is
      // displayed or not.

      if (momentType === 'display') {
        if (!notification.isDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          onError({
            message: getHumanReadableMessage(reason),
            reason,
            type: momentType
          });
        } // `onNotification` will be called whenever Google One Tap successfully
        // initializes. So we mark `googleOneTapIsLoaded` as `true` so that we do not fall
        // into a timed out state.


        if (!googleOneTapIsLoaded.current) {
          googleOneTapIsLoaded.current = true; // Call the `onGoogleOneTapLoaded` callback if one is provided and
          // Google One Tap has successfully loaded
          // We've wrapped it in a check so we only run the callback once.

          if (notification.isDisplayed() && onGoogleOneTapLoaded) {
            onGoogleOneTapLoaded();
          }
        }
      } // Skipped moment: This occurs when the One Tap prompt is closed by an
      // auto cancel, a manual cancel, or when Google fails to issue a
      // credential, such as if the selected session has signed out of Google.
      // In these cases, it's recommended that you continue on to the next
      // identity providers, if there are any.


      if (momentType === 'skipped') {
        const reason = notification.getSkippedReason();
        const errorInfo = {
          message: getHumanReadableMessage(reason),
          reason,
          type: momentType
        };
        onError(errorInfo);
      } // Dismissed moment: This occurs when a credential is successfully
      // retrieved by Google or a user wants to stop the credential retrieval
      // flow. For example, on your login dialog, when the user begins to
      // input their username and password, you can call the
      // google.accounts.id.cancel() method to close the One Tap prompt and
      // trigger a dismissed moment.


      if (momentType === 'dismissed') {
        const reason = notification.getDismissedReason(); // `credential_returned` is returned when Google One Tap has
        // successfully returned a value. In this case, the
        // `googleOneTapOptions.callback` function will be fired.

        if (reason !== 'credential_returned') {
          onError({
            message: getHumanReadableMessage(reason),
            reason,
            type: momentType
          });
        }
      }
    };

    const googleOneTapOptions = {
      auto_select: shouldAutologin,
      callback: result => {
        onSuccess({
          idToken: result.credential
        });
      },
      cancel_on_tap_outside: false,
      client_id: googleClientId,
      context: displayContext,
      prompt_parent_id: GOOGLE_ONE_TAP_PARENT_ID,
      prompt_suppress_sec: GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION,
      state_cookie_domain: 'zapier.com'
    }; // Initialize Google One Tap with the provided configuration

    (_globalAny$window = globalAny.window) === null || _globalAny$window === void 0 ? void 0 : (_globalAny$window$goo = _globalAny$window.google) === null || _globalAny$window$goo === void 0 ? void 0 : (_globalAny$window$goo2 = _globalAny$window$goo.accounts) === null || _globalAny$window$goo2 === void 0 ? void 0 : (_globalAny$window$goo3 = _globalAny$window$goo2.id) === null || _globalAny$window$goo3 === void 0 ? void 0 : _globalAny$window$goo3.initialize(googleOneTapOptions); // Before we prompt Google One Tap, cancel any previous one.

    (_globalAny$window$goo4 = globalAny.window.google.accounts) === null || _globalAny$window$goo4 === void 0 ? void 0 : (_globalAny$window$goo5 = _globalAny$window$goo4.id) === null || _globalAny$window$goo5 === void 0 ? void 0 : _globalAny$window$goo5.cancel(); // This will attempt to render the Google One Tap dialog. `onNotification`
    // will receive notifications that indicate the status of Google One Tap.

    (_globalAny$window2 = globalAny.window) === null || _globalAny$window2 === void 0 ? void 0 : (_globalAny$window2$go = _globalAny$window2.google) === null || _globalAny$window2$go === void 0 ? void 0 : (_globalAny$window2$go2 = _globalAny$window2$go.accounts) === null || _globalAny$window2$go2 === void 0 ? void 0 : (_globalAny$window2$go3 = _globalAny$window2$go2.id) === null || _globalAny$window2$go3 === void 0 ? void 0 : _globalAny$window2$go3.prompt(onNotification);
  }, [displayContext, googleClientId, onError, onGoogleOneTapLoaded, onSuccess, googleOneTapScriptHasLoaded, shouldAutologin]);
  useEffect(() => {
    var _globalAny$document, _globalAny$document2;

    // Add the Google One Tap script to the current context
    const oneTapScript = (_globalAny$document = globalAny.document) === null || _globalAny$document === void 0 ? void 0 : _globalAny$document.createElement('script');
    oneTapScript.setAttribute('type', 'text/javascript');
    oneTapScript.setAttribute('src', 'https://accounts.google.com/gsi/client');
    (_globalAny$document2 = globalAny.document) === null || _globalAny$document2 === void 0 ? void 0 : _globalAny$document2.body.appendChild(oneTapScript); // Once the script has loaded we can begin the configuration process
    // for Google One Tap. If we try to do this immediately, we will encounter
    // a race condition where the script loads after we have attempted to
    // initialize One Tap.

    oneTapScript.onload = () => {
      setGoogleOneTapScriptHasLoaded(true);
    }; // The latest version of Google One Tap is prone to being unable to load
    // for a very long duration. This effectively blocks the UI from loading
    // alternative log-in options such as Google SSO. To avoid this we
    // check to see if One Tap has loaded after a specific duration and if
    // not we fire an error off.


    setTimeout(() => {
      if (!googleOneTapIsLoaded.current) {
        var _globalAny$window3;

        // Fire the cancel function to clear Google One Tap from the DOM just
        // in case it did happen to load async while we are going ahead with the
        // timeout flow.
        if ((_globalAny$window3 = globalAny.window) !== null && _globalAny$window3 !== void 0 && _globalAny$window3.google) {
          var _globalAny$window$goo6, _globalAny$window$goo7;

          (_globalAny$window$goo6 = globalAny.window.google.accounts) === null || _globalAny$window$goo6 === void 0 ? void 0 : (_globalAny$window$goo7 = _globalAny$window$goo6.id) === null || _globalAny$window$goo7 === void 0 ? void 0 : _globalAny$window$goo7.cancel();
        }

        onError({
          message: "Google One Tap was unable to load within the timeout duration, it exceeded the limit of ".concat(GOOGLE_ONE_TAP_LOADING_TIMEOUT, "ms."),
          reason: 'loading_timeout',
          type: 'display'
        });
      }
    }, GOOGLE_ONE_TAP_LOADING_TIMEOUT);
    return () => {
      var _globalAny$window4;

      // Clean up the remains of Google One Tap when the component is unmounted
      if ((_globalAny$window4 = globalAny.window) !== null && _globalAny$window4 !== void 0 && _globalAny$window4.google) {
        var _globalAny$window$goo8, _globalAny$window$goo9;

        (_globalAny$window$goo8 = globalAny.window.google.accounts) === null || _globalAny$window$goo8 === void 0 ? void 0 : (_globalAny$window$goo9 = _globalAny$window$goo8.id) === null || _globalAny$window$goo9 === void 0 ? void 0 : _globalAny$window$goo9.cancel();
      }
    }; // We only want `useEffect` to run on mount, otherwise we might end up
    // attempting to render one tap twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return jsx("div", {
    className: className,
    id: GOOGLE_ONE_TAP_PARENT_ID,
    children: jsx(Global, {
      styles: css(_templateObject$d || (_templateObject$d = _taggedTemplateLiteral(["\n          // Reverses the padding that one tap applies to the desktop mode\n          #credential_picker_container {\n            margin-left: -7px;\n            margin-right: -7px;\n          }\n\n          #google-one-tap-container {\n            z-index: 9;\n          }\n\n          // Allows the Google One Tap iframe to fit the size of the parent\n          #credential_picker_container iframe {\n            display: block;\n            margin: 0 auto;\n            width: ", " !important;\n          }\n\n          // Fixes an issue where the default z-index value is hidden by the\n          // sign up page\n          #credential_picker_iframe {\n            z-index: ", " !important;\n          }\n        "])), width, Zindexes.signUp)
    })
  });
}

/**
 * Attempts to take return from Google One Tap and
 * authenticate into Zapier (will create a new user
 * if required)
 */
async function attemptOneTapSignup(credential, selectedApis) {
  var _response$body, _response$body2;

  const response = await fetch(urls.GOOGLE_ONE_TAP_URL, {
    method: 'POST',
    body: { ...credential,
      selected_apis: selectedApis
    }
  });
  return {
    isNewSignup: (_response$body = response.body) === null || _response$body === void 0 ? void 0 : _response$body.new_signup,
    userId: (_response$body2 = response.body) === null || _response$body2 === void 0 ? void 0 : _response$body2.id
  };
}
/**
 * Hook to deal with the side-effects of using GoogleOneTap
 */


function useGoogleOneTap({
  disableGoogleOneTap = false,
  onSuccess,
  selectedApis
}) {
  const [showGoogleOneTap, setShowGoogleOneTap] = useState(!disableGoogleOneTap);
  const {
    trackEvent,
    trackSignupIntent,
    trackSignupConversion
  } = useTrackingContext();
  const handleGoogleOneTapError = useCallback(function (error) {
    // Track OneTap error
    if (error.type === 'userCanceled' || // Supports Google One Tap V2
    error.reason === 'user_cancel') {
      trackEvent(EVENT_ONE_TAP_GOOGLE_IFRAME_CLOSED, {
        error
      });
      emitGoogleOneTapErrorEvent({
        failure_type: error.type || null,
        failure_reason: error.reason || null,
        message: error.message || null,
        action: 'user_cancel'
      });
    } else {
      trackEvent(EVENT_ONE_TAP_GOOGLE_IFRAME_LOAD_FAILED, {
        error
      });
      emitGoogleOneTapErrorEvent({
        failure_type: error.type || null,
        failure_reason: error.reason || null,
        message: error.message || null,
        action: 'failed_to_load'
      });
      captureMessage("GoogleOneTapError: ".concat(error.message));
    } // hide OneTap since we encountered an error


    setShowGoogleOneTap(false);
  }, [setShowGoogleOneTap, trackEvent]);
  const handleGoogleOneTapSubmit = useCallback(async function (credential) {
    // Track that user is attempting OneTap
    trackSignupIntent('google-one-tap', {
      selected_apis: selectedApis
    });

    try {
      const {
        isNewSignup,
        userId
      } = await attemptOneTapSignup(credential, selectedApis); // Success - track conversion

      trackSignupConversion('google-one-tap', {
        userId,
        selectedApis
      });

      if (onSuccess) {
        onSuccess(isNewSignup);
      }
    } catch (error) {
      // We were unable to auth into Zapier with the return from OneTap
      // handle the error
      handleGoogleOneTapError(error);
    }
  }, [handleGoogleOneTapError, onSuccess, selectedApis, trackSignupConversion, trackSignupIntent]);
  return {
    /** Handler for when GoogleOneTap returns an error */
    handleGoogleOneTapError,

    /** Handler for when GoogleOneTap returns a good response */
    handleGoogleOneTapSubmit,

    /** Should show google one tap */
    showGoogleOneTap
  };
}

function GoogleLogo() {
  return jsxs("svg", {
    height: "21",
    preserveAspectRatio: "xMidYMid",
    viewBox: "0 0 256 262",
    width: "21",
    xmlns: "http://www.w3.org/2000/svg",
    children: [jsx("title", {
      children: "Google Logo"
    }), jsx("path", {
      d: "M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027",
      fill: "#4285F4"
    }), jsx("path", {
      d: "M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1",
      fill: "#34A853"
    }), jsx("path", {
      d: "M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782",
      fill: "#FBBC05"
    }), jsx("path", {
      d: "M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251",
      fill: "#EB4335"
    })]
  });
}

var _templateObject$c;
const rebrandedLinkStyles = css(_templateObject$c || (_templateObject$c = _taggedTemplateLiteral(["\n  &:focus {\n    outline: none;\n  }\n"])));
function GoogleSSOButton({
  children = 'Continue with Google',
  next,
  params = {}
}) {
  return jsx(Link, {
    href: addParamsToUrl('/google-sso/start?scopes=basic', {
      next,
      ...params
    }, true),
    onClick: () => {
      storeAuthType('google');
    },
    css: rebrandedLinkStyles,
    tabIndex: -1,
    children: jsx(SocialAuthButton, {
      tabIndex: undefined,
      color: socialAppColors.google,
      icon: jsx(GoogleLogo, {}),
      children: children
    })
  });
}

function MicrosoftLogo() {
  return jsxs("svg", {
    height: "21",
    viewBox: "0 0 21 21",
    width: "21",
    xmlns: "http://www.w3.org/2000/svg",
    children: [jsx("title", {
      children: "Microsoft Logo"
    }), jsx("rect", {
      fill: "#f25022",
      height: "9",
      width: "9",
      x: "1",
      y: "1"
    }), jsx("rect", {
      fill: "#00a4ef",
      height: "9",
      width: "9",
      x: "1",
      y: "11"
    }), jsx("rect", {
      fill: "#7fba00",
      height: "9",
      width: "9",
      x: "11",
      y: "1"
    }), jsx("rect", {
      fill: "#ffb900",
      height: "9",
      width: "9",
      x: "11",
      y: "11"
    })]
  });
}

function MicrosoftSSOButton({
  children = 'Continue with Microsoft',
  next,
  onTrackSignupConversion,
  onTrackSignupIntent,
  params = {},
  routeTo
}) {
  return jsx(SSOButton, {
    href: getStartUrlForProvider('microsoft'),
    next: next,
    onTrackSignupConversion: onTrackSignupConversion,
    onTrackSignupIntent: onTrackSignupIntent,
    params: params,
    routeTo: routeTo,
    type: "microsoft-sso",
    children: jsx(SocialAuthButton, {
      color: socialAppColors.microsoft,
      icon: jsx(MicrosoftLogo, {}),
      tabIndex: undefined,
      children: children
    })
  });
}

// Initializing zxcvbn
const options = {
  translations: zxcvbnEnPackage.translations,
  dictionary: { ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary
  }
};
ZxcvbnOptions.setOptions(options);
function PasswordStrengthMeter({
  password,
  showHint = false
}) {
  const passwordStrength = useMemo(() => calculatePasswordStrength(password), [password]);
  const barColor = useMemo(() => {
    return getBarColor(passwordStrength);
  }, [passwordStrength]);
  return jsxs("div", {
    children: [jsx(ProgressBar, {
      color: barColor,
      value: passwordStrength.score / 4
    }), showHint && jsx(Text, {
      color: "error",
      type: "smallPrint3",
      children: passwordStrength.hint
    })]
  });
} // used as the default value for password hint text
// can't use the literal '&nbsp;' or else the & would be escaped

const NBSP = '\u00a0';

function calculatePasswordStrength(password) {
  // zxcvbn has a bug where empty passwords result in an error
  const defaultResult = {
    score: 0,
    feedback: {
      warning: ''
    }
  }; // zxcvbn can start to experience lag as longer passwords are typed
  // if a user is manually typing >64 characters, it's highly likely
  // to be a long, high-scoring passphrase OR to be a random string
  // generated by a password manager rather than a low-scoring string
  // of manually typed repeated letters / sequential keyboard keys / etc.
  // so this function will only operate on the last 64 characters

  const {
    score,
    feedback
  } = password ? zxcvbn(password.slice(-64)) : defaultResult; // force 0 -> 1 for aesthetics

  const clampedScore = Math.max(score, 1); // default to `&nbsp;` so the hint text is never empty, which would
  // cause the height of our component to not be consistent.

  const hint = (feedback === null || feedback === void 0 ? void 0 : feedback.warning) || NBSP;
  return {
    score: clampedScore,
    hint
  };
}

function getBarColor(passwordStrength) {
  switch (passwordStrength.score) {
    case 1:
      return 'error';

    case 2:
      return 'warning';

    case 3:
    case 4:
      return 'success';

    default:
      return 'error';
  }
}

var _templateObject$b, _templateObject2$7, _templateObject3$3;
const separatorStyle = css(_templateObject$b || (_templateObject$b = _taggedTemplateLiteral(["\n  border-top: 1px solid ", ";\n  height: 0;\n  width: 100%;\n"])), Colors.gray20);
const wrapperStyles = css(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteral(["\n  align-items: center;\n  display: flex;\n"])));
const textStyles = css(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n  padding: 0 20px;\n"])), Typography.superTitle3, Colors.gray60);
function SocialOptionsSeparator() {
  return jsxs("div", {
    css: wrapperStyles,
    "data-testid": "login-separator",
    children: [jsx("hr", {
      css: separatorStyle
    }), jsx("span", {
      css: textStyles,
      children: "OR"
    }), jsx("hr", {
      css: separatorStyle
    })]
  });
}

var _templateObject$a;
const rootStyle$4 = css(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral(["\n  ", "\n  text-align: center;\n"])), Typography.smallPrint1);
function AltAuthCta({
  authType
}) {
  if (authType === 'email') {
    return jsxs("p", {
      css: rootStyle$4,
      children: ["Or", ' ', jsx(Link, {
        href: addTruthyParamsToUrl(window.location.href, {
          forceLoginDisplayForm: true
        }),
        children: "log in with Google, Facebook, or Microsoft."
      })]
    });
  }

  return jsxs("p", {
    css: rootStyle$4,
    children: ["Or", ' ', jsx(Link, {
      href: addTruthyParamsToUrl(window.location.href, {
        forceLoginDisplayForm: true
      }),
      children: "use your email address to log in."
    })]
  });
}

var _templateObject$9, _templateObject2$6;
const fieldCss$1 = css(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral(["\n  margin-bottom: 20px;\n"]))); // Hide the fields in a very "creative" way so that
// password managers still consider them visible and
// fill them in. `display: none` doesn't work for all
// password managers.

const hiddenFieldCss$1 = css(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteral(["\n  margin-bottom: 0;\n  position: relative;\n  overflow: hidden;\n\n  * {\n    position: absolute !important;\n    left: 100vw !important;\n  }\n"])));
function EmailPasswordForm({
  canSubmit,
  formData,
  formErrors,
  handleChange,
  handleSubmit,
  shouldDisableEmailField,
  shouldFocusEmailField,
  shouldShowEmailField,
  shouldShowPasswordField,
  status,
  submittedFields
}) {
  // Used to focus email field when we first switch to the login page
  const emailRef = useAutoFocus(shouldShowEmailField && shouldFocusEmailField); // Used to focus password field when we first switch to the password page

  const passwordRef = useAutoFocus(shouldShowPasswordField);
  return jsxs("form", {
    onSubmit: handleSubmit,
    children: [jsx("div", {
      css: [fieldCss$1, !shouldShowEmailField && hiddenFieldCss$1],
      "aria-hidden": !shouldShowEmailField,
      "data-testid": "emailField-wrapper",
      children: jsx(Field, {
        error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.email ? formErrors.email : '',
        label: "Email",
        inputId: "login-email",
        isRequired: true,
        renderInput: inputProps => jsx(TextInput, { ...inputProps,
          autoComplete: "email",
          isDisabled: shouldDisableEmailField || status === 'submitting',
          isFocusable: shouldShowEmailField,
          inputMode: "email",
          name: "email",
          onChange: e => handleChange('email', e.target.value),
          placeholder: "Email",
          type: "email",
          value: formData.email,
          ref: emailRef
        })
      })
    }), jsx("div", {
      css: [fieldCss$1, !shouldShowPasswordField && hiddenFieldCss$1],
      "aria-hidden": !shouldShowPasswordField,
      "data-testid": "passwordField-wrapper",
      children: jsx(Field, {
        error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.password ? formErrors.password : '',
        label: "Password",
        inputId: "login-password",
        isRequired: Boolean(shouldShowPasswordField),
        renderInput: inputProps => jsx(TextInput, { ...inputProps,
          autoComplete: "current-password",
          ref: passwordRef,
          isDisabled: status === 'submitting',
          isFocusable: shouldShowPasswordField,
          name: "password",
          onChange: e => handleChange('password', e.target.value),
          placeholder: "Password",
          type: "password",
          value: formData.password
        })
      })
    }), shouldShowPasswordField && jsxs(Fragment, {
      children: [jsx(Link, {
        href: urls.FORGOT_PASSWORD_URL,
        children: "Forgot Password?"
      }), jsx(Spacer, {
        height: 20
      })]
    }), jsx(AuthButton, {
      disabled: !canSubmit || status === 'submitting',
      isBusy: status === 'submitting',
      type: "submit",
      children: jsx("span", {
        "data-testid": "email-password-continue",
        children: "Continue"
      })
    })]
  });
}

var _templateObject$8;
const noAccountLinkStyles = css(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n  text-align: center;\n"])), Typography.smallPrint1, Colors.gray80);
function NoAccountLink({
  queryParams
}) {
  return jsxs("p", {
    css: noAccountLinkStyles,
    children: ["Don't have a Zapier account yet?", ' ', jsx(Link, {
      color: 'primary',
      href: addParamsToUrl(urls.SIGNUP_URL, queryParams),
      children: "Sign Up"
    })]
  });
}

function SocialItems$1({
  next,
  selectedApis = [],
  shouldShowFacebookSSO,
  shouldShowGoogleSSO,
  shouldShowMicrosoftSSO
}) {
  const {
    trackSignupConversion,
    trackSignupIntent
  } = useTrackingContext();
  const onTrackSignupConversion = useCallback(async (type, userId) => {
    return trackSignupConversion(type, {
      selectedApis,
      userId
    });
  }, [selectedApis, trackSignupConversion]);
  const onTrackSignupIntent = useCallback(async type => {
    return trackSignupIntent(type, {
      selectedApis
    });
  }, [selectedApis, trackSignupIntent]); // You may see this and think, this should be a grid, so it's easier to maintain. And you'd be right. The thing is though,
  // If we don't wrap all of these in a div, a weird bug causes the SSO buttons to not render correctly. If it's in a grid
  // and we wrap it in a div, it works fine but the spacing is wrong. This is a hack to get around that.

  return jsxs("div", {
    children: [jsx("div", {
      children: shouldShowGoogleSSO && jsxs(Fragment, {
        children: [jsx(GoogleSSOButton, {
          next: next
        }), (shouldShowFacebookSSO || shouldShowMicrosoftSSO) && jsx(Spacer, {
          height: 20
        })]
      })
    }), jsx("div", {
      children: shouldShowFacebookSSO && jsxs(Fragment, {
        children: [jsx(FacebookSSOButton, {
          next: next,
          onTrackSignupConversion: onTrackSignupConversion,
          onTrackSignupIntent: onTrackSignupIntent
        }), shouldShowMicrosoftSSO && jsx(Spacer, {
          height: 20
        })]
      })
    }), jsx("div", {
      children: shouldShowMicrosoftSSO && jsx(MicrosoftSSOButton, {
        next: next,
        onTrackSignupConversion: onTrackSignupConversion,
        onTrackSignupIntent: onTrackSignupIntent
      })
    })]
  });
}

var _templateObject$7, _templateObject2$5;
const linkStyle = css(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  white-space: nowrap;\n"])));
const rootStyle$3 = css(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteral(["\n  ", "\n  text-align: center;\n"])), Typography.smallPrint1Semibold);
function WelcomeBackMessage({
  email,
  onClear
}) {
  if (email) {
    return jsxs("p", {
      css: rootStyle$3,
      children: ["Welcome back ", email, "!", ' ', jsx("span", {
        css: linkStyle,
        children: jsx(Link, {
          onClick: onClear,
          children: "Not you?"
        })
      })]
    });
  }

  return jsx("p", {
    css: rootStyle$3,
    children: "Welcome back!"
  });
}

var _templateObject$6, _templateObject2$4;
const errorStyle$2 = css(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n"])), Typography.base, Colors.error);
const rootStyle$2 = css(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n  display: grid;\n  grid-gap: 25px;\n"])));
/**
 * Displays form to allow the user to log in. Supports username / password, SSO, SAML
 */

function LoginForm({
  enableEmailAutoFocus = false,
  forceLoginDisplayForm = false,
  googleCaptchaSiteKey,
  invitation,
  next = urls.DEFAULT_LOGIN_NEXT,
  queryParams = {},
  routeTo,
  showSignupLink = true,
  googleSSOEnabled = true,
  microsoftSSOEnabled = true,
  facebookSSOEnabled = true
}) {
  const {
    canSubmit,
    formData,
    formErrors,
    handleChange,
    handleClearForm,
    handleSubmit,
    isCaptchaRequired,
    previousAuthType,
    shouldShowEmailPasswordForm,
    shouldShowEmailField,
    shouldShowFacebookSSO,
    shouldShowGoogleSSO,
    shouldShowMicrosoftSSO,
    shouldShowPasswordField,
    status,
    submittedFields
  } = useLogin({
    email: invitation === null || invitation === void 0 ? void 0 : invitation.email,
    defaultEmail: (queryParams === null || queryParams === void 0 ? void 0 : queryParams.email) || '',
    forceLoginDisplayForm,
    next,
    routeTo,
    googleSSOEnabled,
    microsoftSSOEnabled,
    facebookSSOEnabled
  });
  const showSSOButtons = [googleSSOEnabled, microsoftSSOEnabled, facebookSSOEnabled].find(enabled => enabled === true || enabled === undefined);
  const hasSocial = shouldShowFacebookSSO || shouldShowGoogleSSO || shouldShowMicrosoftSSO;
  const isWelcomeBackShown = (!forceLoginDisplayForm || submittedFields.email) && (!shouldShowEmailField || previousAuthType);
  const isSeparatorShown = shouldShowEmailPasswordForm && shouldShowEmailField && hasSocial && showSSOButtons;
  const isAltAuthCtaShown = (!forceLoginDisplayForm || submittedFields.email) && previousAuthType && shouldShowEmailField; // Only show Captcha if it's required

  if (isCaptchaRequired) {
    return jsx(Captcha, {
      googleCaptchaSiteKey: googleCaptchaSiteKey,
      onCaptchaVerification: captcha => {
        handleChange('captcha', captcha || '');
      }
    });
  }

  return jsxs("section", {
    css: rootStyle$2,
    children: [isWelcomeBackShown && jsx(WelcomeBackMessage, {
      email: previousAuthType && shouldShowEmailField ? '' : formData.email,
      onClear: handleClearForm
    }), hasSocial && shouldShowEmailField && showSSOButtons && jsx(SocialItems$1, {
      next: next,
      selectedApis: (queryParams === null || queryParams === void 0 ? void 0 : queryParams.selectedApis) || [],
      shouldShowFacebookSSO: shouldShowFacebookSSO,
      shouldShowGoogleSSO: shouldShowGoogleSSO,
      shouldShowMicrosoftSSO: shouldShowMicrosoftSSO
    }), isSeparatorShown && jsx(SocialOptionsSeparator, {}), shouldShowEmailPasswordForm && jsx(EmailPasswordForm, {
      canSubmit: canSubmit,
      formData: formData,
      formErrors: formErrors,
      handleChange: handleChange,
      handleSubmit: handleSubmit,
      shouldDisableEmailField: Boolean(invitation === null || invitation === void 0 ? void 0 : invitation.email),
      shouldFocusEmailField: Boolean(enableEmailAutoFocus),
      shouldShowEmailField: shouldShowEmailField,
      shouldShowPasswordField: shouldShowPasswordField,
      status: status,
      submittedFields: submittedFields
    }), formErrors.error && status === 'error' && jsx("div", {
      css: errorStyle$2,
      children: formErrors.error
    }), isAltAuthCtaShown && previousAuthType && showSSOButtons && jsx(AltAuthCta, {
      authType: previousAuthType
    }), showSignupLink && jsx(NoAccountLink, {
      queryParams: queryParams
    })]
  });
}

var _templateObject$5;
const rootStyle$1 = css(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  ", "\n  text-align: center;\n"])), Typography.base);
function EmailMessage({
  email,
  onClick
}) {
  return jsxs("p", {
    css: rootStyle$1,
    children: ["Signing up as ", email, ". ", jsx(Link, {
      onClick: onClick,
      children: "Not right?"
    })]
  });
}

var _templateObject$4;
const textStyle = css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n"])), Typography.smallPrint1, Colors.neutral700);
function Legal() {
  return jsxs("p", {
    css: textStyle,
    children: ["By signing up, you agree to Zapier's", ' ', jsx(Link, {
      href: urls.LEGAL_URL,
      target: "_blank",
      children: "terms of service"
    }), ' ', "and", ' ', jsx(Link, {
      href: urls.PRIVACY_URL,
      target: "_blank",
      children: "privacy policy."
    })]
  });
}

var _templateObject$3, _templateObject2$3, _templateObject3$2, _templateObject4$1;
const rootCss = css(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  display: grid;\n  grid-gap: 25px;\n"])));
const titleCss = css(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n  ", "\n  text-align: center;\n"])), Typography.base);
const linkCss = css(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  text-align: center;\n"])));
const authLinkCss = css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n  text-decoration: none;\n"])));
function LoggedInMessage({
  currentUser,
  onClick
}) {
  return jsxs("div", {
    css: rootCss,
    children: [jsxs("p", {
      css: titleCss,
      children: ["You are currently logged in as ", currentUser.email]
    }), jsx(Link, {
      href: urls.DEFAULT_LOGIN_NEXT,
      css: authLinkCss,
      children: jsx(AuthButton, {
        children: "Continue"
      })
    }), jsx("div", {
      css: linkCss,
      children: jsx(Link, {
        onClick: onClick,
        children: "Sign up as a different person"
      })
    })]
  });
}

function SocialItems({
  googleClientId,
  isSignupFromInvitation,
  next,
  routeTo,
  selectedApis,
  useCaseSlug,
  shouldShowFacebookSSO,
  shouldShowGoogleSSO,
  shouldShowMicrosoftSSO,
  invitation
}) {
  const params = {};

  if (invitation) {
    params.invitation = invitation;
  }

  if (selectedApis) {
    params.selected_apis = selectedApis;
  }

  const onGoogleOneTapSuccess = useCallback(isNewSignup => {
    storeAuthType('google');
    routeTo(next || (isNewSignup ? urls.DEFAULT_SIGNUP_NEXT : urls.DEFAULT_LOGIN_NEXT));
  }, [next, routeTo]);
  const oneTap = useGoogleOneTap({
    disableGoogleOneTap: !googleClientId,
    onSuccess: onGoogleOneTapSuccess,
    selectedApis
  });
  const {
    trackSignupConversion,
    trackSignupIntent
  } = useTrackingContext();
  const onTrackSignupConversion = useCallback((type, userId) => {
    return trackSignupConversion(type, {
      selectedApis,
      useCaseSlug,
      userId
    });
  }, [selectedApis, trackSignupConversion, useCaseSlug]);
  const onTrackSignupIntent = useCallback(type => {
    trackSignupIntent(type, {
      selectedApis
    });
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_intent',
      signupType: oldSignupTypeToAvroSignupType(type),
      selectedApis
    }).catch(captureException);
  }, [selectedApis, trackSignupIntent]); // You may see this and think, this should be a grid, so it's easier to maintain. And you'd be right. The thing is though,
  // If we don't wrap all of these in a div, a weird bug causes the SSO buttons to not render correctly. If it's in a grid
  // and we wrap it in a div, it works fine but the spacing is wrong. This is a hack to get around that.

  return jsxs("div", {
    children: [jsx("div", {
      children: shouldShowGoogleSSO && (!isCypress() && oneTap.showGoogleOneTap && !isSignupFromInvitation ? jsxs(Fragment, {
        children: [jsx(GoogleOneTap, {
          displayContext: "signup",
          googleClientId: googleClientId,
          onError: oneTap.handleGoogleOneTapError,
          onSuccess: oneTap.handleGoogleOneTapSubmit,
          shouldAutologin: false
        }), shouldShowFacebookSSO && jsx(Spacer, {
          height: 10
        })]
      }) : jsxs(Fragment, {
        children: [jsx(GoogleSSOButton, {
          next: next,
          params: params,
          children: "Sign up with Google"
        }), shouldShowFacebookSSO && jsx(Spacer, {
          height: 20
        })]
      }))
    }), jsx("div", {
      children: shouldShowFacebookSSO && jsxs(Fragment, {
        children: [jsx(FacebookSSOButton, {
          next: next,
          onTrackSignupConversion: onTrackSignupConversion,
          onTrackSignupIntent: onTrackSignupIntent,
          params: params,
          children: "Sign up with Facebook"
        }), shouldShowMicrosoftSSO && jsx(Spacer, {
          height: 20
        })]
      })
    }), jsx("div", {
      children: shouldShowMicrosoftSSO && jsx(MicrosoftSSOButton, {
        next: next,
        onTrackSignupConversion: onTrackSignupConversion,
        onTrackSignupIntent: onTrackSignupIntent,
        params: params,
        children: "Sign up with Microsoft"
      })
    })]
  });
}

var _templateObject$2, _templateObject2$2, _templateObject3$1, _templateObject4, _templateObject5, _templateObject6;
const fieldCss = css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  margin-bottom: 15px;\n"]))); // Hide the fields in a very "creative" way so that
// password managers still consider them visible and
// fill them in. `display: none` doesn't work for all
// password managers.

const hiddenFieldCss = css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  margin-bottom: 0;\n  position: relative;\n  overflow: hidden;\n\n  * {\n    position: absolute !important;\n    left: 100vw !important;\n  }\n"])));
const nameWrapperStyles = css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  display: grid;\n  grid-gap: 15px;\n  ", " {\n    grid-template-columns: 1fr 1fr;\n  }\n"])), mediaQueries.desktop);
const nameStyles = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  min-width: 1px;\n\n  * {\n    min-width: 1px !important;\n  }\n"])));
const hashbrownPromotion = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  margin-bottom: 15px;\n  font-weight: 400;\n  font-size: 14px;\n  line-height: 20px;\n  color: #403f3e;\n"])));
const promotionMessageCss = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  display: flex;\n  gap: 5px;\n"])));
function UserInformation({
  formData,
  formErrors,
  handleChange,
  handleFocus,
  handleSubmit,
  shouldDisableEmailField,
  shouldFocusEmailField,
  shouldShowEmailField,
  shouldShowNameFields,
  shouldShowPasswordField,
  submittedFields,
  status,
  campaign
}) {
  // Used to focus email field when we first switch to the signup page
  const emailRef = useAutoFocus(shouldShowEmailField && shouldFocusEmailField); // Used to focus password field when we first switch to the password page

  const passwordRef = useAutoFocus(shouldShowPasswordField);
  return jsxs("form", {
    onSubmit: handleSubmit,
    children: [jsx("div", {
      css: [fieldCss, !shouldShowEmailField && hiddenFieldCss],
      "aria-hidden": !shouldShowEmailField,
      "data-testid": "emailField-wrapper",
      children: jsx(Field, {
        error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.email ? formErrors.email : '',
        label: "Work Email",
        inputId: "signup-email",
        isRequired: true,
        renderInput: inputProps => jsx(TextInput, { ...inputProps,
          autoComplete: "email",
          isDisabled: shouldDisableEmailField || status === 'submitting',
          isFocusable: shouldShowEmailField,
          inputMode: "email",
          name: "email",
          onChange: e => handleChange('email', e.target.value),
          onFocus: handleFocus,
          placeholder: "Work Email",
          type: "email",
          value: formData.email,
          ref: emailRef
        })
      })
    }), campaign === CAMPAIGN_PROJECT_HASHBROWN && status === 'error' && (submittedFields === null || submittedFields === void 0 ? void 0 : submittedFields.email) && formErrors.email && jsxs("div", {
      css: promotionMessageCss,
      children: [jsx(Icon, {
        name: "alertInfo",
        size: 18
      }), jsx("p", {
        css: hashbrownPromotion,
        children: "We aren't currently offering this promotion to users with existing Zapier accounts."
      })]
    }), jsxs("div", {
      css: [fieldCss, nameWrapperStyles, !shouldShowNameFields && hiddenFieldCss],
      "aria-hidden": !shouldShowNameFields,
      "data-testid": "nameFields-wrapper",
      children: [jsx("div", {
        css: nameStyles,
        children: jsx(Field, {
          error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.firstName ? formErrors.firstName : '',
          label: "First Name",
          inputId: "signup-first-name",
          isRequired: true,
          renderInput: inputProps => jsx(TextInput, { ...inputProps,
            autoComplete: "given-name",
            isDisabled: status === 'submitting',
            isFocusable: shouldShowNameFields,
            name: "first_name",
            onChange: e => handleChange('firstName', e.target.value),
            onFocus: handleFocus,
            placeholder: "First Name",
            type: "text",
            value: formData.firstName
          })
        })
      }), jsx("div", {
        css: nameStyles,
        children: jsx(Field, {
          error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.lastName ? formErrors.lastName : '',
          label: "Last Name",
          inputId: "signup-last-name",
          isRequired: true,
          renderInput: inputProps => jsx(TextInput, { ...inputProps,
            autoComplete: "family-name",
            isDisabled: status === 'submitting',
            isFocusable: shouldShowNameFields,
            name: "last_name",
            onChange: e => handleChange('lastName', e.target.value),
            onFocus: handleFocus,
            placeholder: "Last Name",
            type: "text",
            value: formData.lastName
          })
        })
      })]
    }), jsx("div", {
      css: [fieldCss, !shouldShowPasswordField && hiddenFieldCss],
      "aria-hidden": !shouldShowPasswordField,
      "data-testid": "passwordField-wrapper",
      children: jsx(Field, {
        error: status === 'error' && submittedFields !== null && submittedFields !== void 0 && submittedFields.password ? formErrors.password : '',
        label: "Password",
        inputId: "signup-password",
        isRequired: Boolean(shouldShowPasswordField),
        renderInput: inputProps => jsx(TextInput, { ...inputProps,
          autoComplete: "new-password",
          isDisabled: status === 'submitting',
          isFocusable: shouldShowPasswordField,
          name: "password",
          onChange: e => handleChange('password', e.target.value),
          onFocus: handleFocus,
          placeholder: "Password",
          type: "password",
          value: formData.password,
          ref: passwordRef
        })
      })
    }), jsx("div", {
      css: [!shouldShowPasswordField && hiddenFieldCss],
      "aria-hidden": !shouldShowPasswordField,
      "data-testid": "passwordStrengthMeter-wrapper",
      children: jsx(PasswordStrengthMeter, {
        password: formData.password,
        showHint: true
      })
    }), jsx(AuthButton, {
      disabled: status === 'submitting',
      isBusy: status === 'submitting',
      type: "submit",
      children: "Get Started Free"
    })]
  });
}

function useCurrentUser() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState();
  useEffect(() => {
    async function getCurrentSession() {
      const results = await fetch(urls.CURRENT_SESSION_URL);
      setIsLoading(false);
      setCurrentUser(results.body);
    }

    getCurrentSession();
  }, []);
  return {
    currentUser,
    isLoading
  };
}

function SAMLSSOButton({
  next,
  samlUrl,
  onTrackSignupConversion,
  onTrackSignupIntent,
  params = {},
  routeTo
}) {
  return jsx(SSOButton, {
    href: samlUrl,
    next: next,
    onTrackSignupConversion: onTrackSignupConversion,
    onTrackSignupIntent: onTrackSignupIntent,
    params: params,
    routeTo: routeTo,
    type: "sso",
    shouldOpenInChildWindow: false,
    children: jsx(Button, {
      color: socialAppColors.saml,
      tabIndex: undefined,
      children: "Continue with SSO"
    })
  });
}

var _templateObject$1, _templateObject2$1;
const errorStyle$1 = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n"])), Typography.base, Colors.error);
const headerStyles = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n  margin-bottom: 16px;\n  text-align: center;\n\n  ", " {\n    text-align: left;\n  }\n\n  ", " {\n    ", "\n  }\n"])), Typography.paragraph1ShortMedium, Colors.neutral800, mediaQueries.tablet, mediaQueries.desktop, Typography.paragraph1ShortMedium);
/**
 * Displays form to allow the user to sign up. Supports username / password, SSO, GoogleOneTap, SAML
 */

function SignUpForm({
  enableEmailAutoFocus = false,
  googleCaptchaSiteKey,
  googleClientId,
  hideFieldsUntilFocus = false,
  invitation,
  next,
  useCase,
  routeTo,
  campaign,
  selectedApis,
  microsoftSSOEnabled = true,
  facebookSSOEnabled = true,
  googleSSOEnabled = true
}) {
  var _user$currentUser;

  const [ignoreCurrentUser, setIgnoreCurrentUser] = useState(false);
  const {
    formData,
    formErrors,
    handleChange,
    handleClearForm,
    handleFocus: signupHandleFocus,
    handleSubmit,
    isCaptchaRequired,
    shouldShowEmailField,
    shouldShowNameFields,
    shouldShowPasswordField,
    status,
    submittedFields,
    shouldShowFacebookSSO,
    shouldShowGoogleSSO,
    shouldShowMicrosoftSSO
  } = useSignup({
    email: invitation === null || invitation === void 0 ? void 0 : invitation.email,
    hideFieldsUntilFocus,
    next,
    routeTo,
    selectedApis,
    invitation: invitation === null || invitation === void 0 ? void 0 : invitation.code,
    microsoftSSOEnabled,
    facebookSSOEnabled,
    googleSSOEnabled
  });
  const signupWithSAML = invitation === null || invitation === void 0 ? void 0 : invitation.account.hasSamlSso;
  let samlSignUpUrl = '';

  if (signupWithSAML && invitation) {
    samlSignUpUrl = addParamsToUrl(invitation.ssoUrl, {
      invitation: invitation.code
    });
  }

  const user = useCurrentUser();

  const handleSignupFormFocus = () => {
    signupHandleFocus();
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup_intent',
      signupType: 'direct',
      selectedApis
    }).catch(captureException);
  };

  function handleSubmitWithEvents(e) {
    dispatchSignupUserInteractionEvent({
      signupFunnel: 'signup',
      signupType: 'direct',
      selectedApis
    }).catch(captureException);
    return handleSubmit(e);
  } // Warn the user if they are currently logged in before allowing
  // them to sign up


  if ((_user$currentUser = user.currentUser) !== null && _user$currentUser !== void 0 && _user$currentUser.email && !ignoreCurrentUser) {
    return jsx(LoggedInMessage, {
      currentUser: user.currentUser,
      onClick: () => setIgnoreCurrentUser(true)
    });
  } // Only show Captcha if it's required


  if (isCaptchaRequired) {
    return jsx(Captcha, {
      googleCaptchaSiteKey: googleCaptchaSiteKey,
      onCaptchaVerification: captcha => {
        handleChange('captcha', captcha || '');
      }
    });
  }

  return jsxs(Grid, {
    gap: 20,
    children: [shouldShowPasswordField && !signupWithSAML && jsx(EmailMessage, {
      email: formData.email,
      onClick: handleClearForm
    }), shouldShowEmailField && !signupWithSAML && jsx(SocialItems, {
      googleClientId: googleClientId // Check the invitation parameter and not the object to avoid a
      // initial false value while we're fetching the invitation object
      ,
      isSignupFromInvitation: !!invitation,
      next: next,
      routeTo: routeTo || defaultRouteTo$1,
      selectedApis: selectedApis,
      useCaseSlug: useCase,
      shouldShowFacebookSSO: shouldShowFacebookSSO,
      shouldShowGoogleSSO: shouldShowGoogleSSO,
      shouldShowMicrosoftSSO: shouldShowMicrosoftSSO,
      invitation: invitation === null || invitation === void 0 ? void 0 : invitation.code
    }), signupWithSAML && jsxs(Grid, {
      gap: 20,
      children: [jsx("h2", {
        css: headerStyles,
        children: "Sign up with your Identity Provider"
      }), jsx(SAMLSSOButton, {
        samlUrl: samlSignUpUrl,
        next: next
      })]
    }), shouldShowEmailField && !signupWithSAML && jsx(SocialOptionsSeparator, {}), !signupWithSAML && jsx(UserInformation, {
      formData: formData,
      formErrors: formErrors,
      handleChange: handleChange,
      handleFocus: handleSignupFormFocus,
      handleSubmit: handleSubmitWithEvents,
      shouldDisableEmailField: Boolean(invitation === null || invitation === void 0 ? void 0 : invitation.email),
      shouldFocusEmailField: Boolean(enableEmailAutoFocus),
      shouldShowEmailField: shouldShowEmailField,
      shouldShowNameFields: shouldShowNameFields,
      shouldShowPasswordField: shouldShowPasswordField,
      status: status,
      submittedFields: submittedFields,
      campaign: campaign
    }), formErrors.error && status === 'error' && jsx("div", {
      css: errorStyle$1,
      children: formErrors.error
    }), jsx(Legal, {})]
  });
}

var _templateObject, _templateObject2, _templateObject3;
const centerText = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  text-align: center;\n"])));
const errorStyle = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  ", "\n  color: ", ";\n"])), Typography.base, Colors.error);
const rootStyle = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: grid;\n  grid-gap: 25px;\n"])));
function TwoFactorAuthenticationForm({
  next,
  queryParams = {},
  routeTo,
  useRecoveryCode = false
}) {
  const codeType = useRecoveryCode ? 'recovery_code' : 'code';
  const {
    canSubmit,
    code,
    error,
    handleChangeCode,
    handleSubmit,
    status
  } = useTwoFactor({
    codeType,
    next,
    routeTo
  });
  const fieldLabel = codeType === 'code' ? 'Authentication Code' : 'Recovery Code';
  const fieldId = codeType === 'code' ? '2fa-code' : '2fa-recovery-code';
  const title = codeType === 'code' ? 'Enter the 6-digit code generated by your authentication app.' : 'Enter a recovery code that you saved when setting up 2FA.'; // Used to focus the code field when we show the page.
  // We need 2 different ref to keep the autofocus on the field when we switch
  // from Authentication code to Recovery code and viceversa.

  const authCodeRef = useAutoFocus(codeType === 'code');
  const recoveryCodeRef = useAutoFocus(codeType !== 'code');
  return jsxs("form", {
    css: rootStyle,
    onSubmit: handleSubmit,
    children: [jsx("span", {
      css: centerText,
      children: jsx(Text, {
        color: 'neutral800',
        type: "paragraph1",
        children: title
      })
    }), jsx(Field, {
      label: fieldLabel,
      inputId: fieldId,
      isDisabled: status !== 'initial',
      isRequired: true,
      renderInput: inputProps => jsx(TextInput, { ...inputProps,
        autoComplete: "one-time-code",
        inputMode: "numeric",
        onChange: e => handleChangeCode(e.target.value),
        placeholder: fieldLabel,
        value: code,
        ref: codeType === 'code' ? authCodeRef : recoveryCodeRef
      })
    }), jsx(AuthButton, {
      disabled: !canSubmit || status !== 'initial',
      isBusy: status === 'submitting',
      type: "submit",
      children: "Confirm"
    }), error && jsx("div", {
      css: errorStyle,
      children: error
    }), codeType === 'code' && jsx("span", {
      css: centerText,
      children: jsx(Link, {
        href: addParamsToUrl(urls.TWO_FACTOR_URL, { ...queryParams,
          use_recovery_code: true
        }),
        children: "Use a recovery code"
      })
    }), codeType === 'recovery_code' && jsx("span", {
      css: centerText,
      children: jsx(Link, {
        href: addParamsToUrl(urls.TWO_FACTOR_URL, { ...queryParams,
          use_recovery_code: false
        }),
        children: "Use a 2FA code"
      })
    }), jsx("span", {
      css: centerText,
      children: jsx(Link, {
        href: removeParamsFromUrl(addParamsToUrl(urls.LOGIN_URL, queryParams), ['use_recovery_code']),
        children: "Log in as a different user"
      })
    })]
  });
}

export { AUTH_TYPE_COOKIE_NAME, AuthButton, CAMPAIGN_PROJECT_HASHBROWN, Captcha, DASHBOARD_FALLBACK_URL_NAME, FacebookSSOButton, GoogleOneTap, GoogleSSOButton, LOCAL_STORAGE_ACCOUNT_ID, LoginForm, LoginResultType, MicrosoftSSOButton, PasswordStrengthMeter, SignUpForm, SignupResultType, TwoFactorAuthenticationForm, removeStoredAuthType, signupTypeToAuthType, storeAuthType, useAuthType, useAutoFocus, useGoogleOneTap, useInvitation, useLogin, useSignup, useTwoFactor };
